import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, updateDoc, deleteDoc, serverTimestamp, Timestamp,
  getDocs, collection, query, where, orderBy, onSnapshot, addDoc,
  writeBatch, deleteField
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from '../firebase';
import { useAuth } from '../context/AuthContext';
import {
  getStatusClassSuffix, formatDate, getPriorityClassSuffix, formatRelativeTime,
  formatDurationBetweenDates
} from '../utils/styleUtils';
import styles from '../styles/TaskDetailPage.module.css';
import RejectionModal from '../components/RejectionModal';
import ExtensionRejectionModal from '../components/ExtensionRejectionModal';

// Role Definitions
const managerRolesForView = ['Manager', 'Accounts Manager'];
const statusUpdateRoles = ['Manager', 'Accounts Manager'];
const companyApprovalRoles = ['Manager', 'Accounts Manager', 'Sales'];
const customerApprovalRoles = ['Manager', 'Accounts Manager', 'Sales'];
const extensionResponseRoles = ['Manager', 'Accounts Manager'];
const deleteTaskRoles = ['Manager'];

// Constants
const DEFAULT_AVATAR = '/default-avatar.png';
const MAX_UPLOAD_SIZE_MB = 10;
const MAX_UPLOAD_COUNT = 10;

const TaskDetailPage = () => {
  const { taskId } = useParams();
  const { currentUser, userRole } = useAuth();
  const navigate = useNavigate();

  // --- Component State ---
  const [task, setTask] = useState(null);
  const [assignees, setAssignees] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [creatorInfo, setCreatorInfo] = useState({ name: '', photoURL: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Action-specific loading states
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPreparingEmail, setIsPreparingEmail] = useState(false);
  const [isAddingComment, setIsAddingComment] = useState(false);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [isSubmittingWork, setIsSubmittingWork] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isCustomerApproving, setIsCustomerApproving] = useState(false);
  const [isCustomerRejecting, setIsCustomerRejecting] = useState(false);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  // Extension Request States
  const [showExtensionRequestForm, setShowExtensionRequestForm] = useState(false);
  const [requestedEndDate, setRequestedEndDate] = useState('');
  const [extensionRequestReason, setExtensionRequestReason] = useState('');
  const [isSubmittingExtensionRequest, setIsSubmittingExtensionRequest] = useState(false);
  const [extensionRequestError, setExtensionRequestError] = useState('');
  const [isRespondingToExtension, setIsRespondingToExtension] = useState(false);
  const [showExtensionRejectionModal, setShowExtensionRejectionModal] = useState(false);

  // Form states
  const [newStatus, setNewStatus] = useState('');
  const [newComment, setNewComment] = useState('');
  const [submissionText, setSubmissionText] = useState('');
  const [linkInputs, setLinkInputs] = useState(['']);
  const [submissionFiles, setSubmissionFiles] = useState([]);
  const [filePreviews, setFilePreviews] = useState([]);

  // Comments state
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [commentError, setCommentError] = useState('');

  // Mention State
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [showMentionSuggestions, setShowMentionSuggestions] = useState(false);
  const [mentionCursorPosition, setMentionCursorPosition] = useState(0);
  const newCommentRef = useRef(null);

  // Feedback states
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState('');
  const [approvalError, setApprovalError] = useState('');
  const [approvalSuccess, setApprovalSuccess] = useState('');
  const [customerApprovalError, setCustomerApprovalError] = useState('');
  const [customerApprovalSuccess, setCustomerApprovalSuccess] = useState('');

  // Modal State
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showCustomerRejectionModal, setShowCustomerRejectionModal] = useState(false);

  // Activity Log State
  const [activityLog, setActivityLog] = useState([]);
  const [activityLoading, setActivityLoading] = useState(true);
  const [activityError, setActivityError] = useState('');

  // Refs
  const commentsEndRef = useRef(null);
  const isMounted = useRef(true);
  const successTimeoutRef = useRef(null);
  const fileInputRef = useRef(null);

  const statusOptions = ["Not Started", "In Progress", "Company Approval", "Customer Approval", "Completed", "Cancelled"];
  const isProcessingAction = updatingStatus || isDeleting || isPreparingEmail || isAddingComment || !!deletingCommentId || isAcknowledging || isSubmittingWork || isApproving || isRejecting || isCustomerApproving || isCustomerRejecting || isUploadingFile || isSubmittingExtensionRequest || isRespondingToExtension;

  // --- Staff Profile Modal state + helpers ---
  const [staffModalOpen, setStaffModalOpen] = useState(false);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffError, setStaffError] = useState('');
  const [staffProfile, setStaffProfile] = useState(null); // { id, name, email, role, photoURL, phone, department, joinedAt }

  const openStaffModal = async (userId) => {
    setStaffError('');
    setStaffLoading(true);
    setStaffModalOpen(true);

    try {
      // use cached list for immediate paint
      const fromAll = allUsers.find(u => u.id === userId);
      const base = fromAll || assignees.find(a => a.id === userId) || { id: userId };
      setStaffProfile(base);

      const userRef = doc(db, 'users', userId);
      const snap = await getDoc(userRef);
      if (snap.exists()) {
        const d = snap.data();
        setStaffProfile({
          id: userId,
          name: d.name || base.name || d.email || 'Unknown',
          email: d.email || base.email || '',
          // role: d.role || base.role || 'Unknown',
          photoURL: d.photoURL || base.photoURL || null,
          phone: d.phone || d.phoneNumber || '',
          department: d.department || '',
          joinedAt: d.joinedAt?.toDate?.() || null,
        });
      } else {
        setStaffError('User not found.');
      }
    } catch (e) {
      console.error('Fetch staff profile failed:', e);
      setStaffError('Failed to load staff info.');
    } finally {
      setStaffLoading(false);
    }
  };

  const closeStaffModal = () => {
    setStaffModalOpen(false);
    setStaffProfile(null);
    setStaffError('');
  };

  // --- Effects: previews cleanup ---
  useEffect(() => {
    isMounted.current = true;
    const currentLocalPreviews = [...filePreviews];
    return () => {
      isMounted.current = false;
      if (successTimeoutRef.current) { clearTimeout(successTimeoutRef.current); }
      currentLocalPreviews.forEach(preview => {
        if (preview.url && preview.url.startsWith('blob:')) {
          URL.revokeObjectURL(preview.url);
        }
      });
    };
  }, [filePreviews]);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (comments.length > 0 && !commentsLoading) { commentsEndRef.current?.scrollIntoView({ behavior: "smooth" }); }
  }, [comments, commentsLoading]);

  // Mark comments as "seen" for this user whenever they open the task (and when a new comment
  // arrives while viewing). The dashboard uses commentsSeenAt vs lastCommentAt to flag unseen
  // comments on a task, so the employee notices. Best-effort; never blocks the page.
  useEffect(() => {
    if (!currentUser?.uid || !taskId || commentsLoading) return;
    if (!task) return;
    const isParticipant =
      task.createdBy === currentUser.uid ||
      (task.assignedTo || []).includes(currentUser.uid) ||
      managerRolesForView.includes(userRole);
    if (!isParticipant) return;
    updateDoc(doc(db, 'tasks', taskId), { [`commentsSeenAt.${currentUser.uid}`]: serverTimestamp() })
      .catch((e) => console.warn('mark comments seen failed:', e?.message || e));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, comments.length, commentsLoading, currentUser, userRole]);

  // Fetch user list for mentions
  useEffect(() => {
    const fetchAllUsers = async () => {
      if (!isMounted.current) return;
      try {
        const usersCol = collection(db, 'users');
        const q = query(usersCol, orderBy('name', 'asc'));
        const snapshot = await getDocs(q);
        if (!isMounted.current) return;
        const userList = snapshot.docs.map(doc => ({
          id: doc.id,
          name: doc.data().name || doc.data().email,
          email: doc.data().email,
          // role: doc.data().role || 'Unknown',
          photoURL: doc.data().photoURL || null
        }));
        setAllUsers(userList);
      } catch (err) {
        console.error("Error fetching all users for mentions:", err);
        if(isMounted.current) setError(prev => `${prev ? prev + " | " : ""}Failed to load user list for mentions.`);
      }
    };
    fetchAllUsers();
  }, []);

  // Task snapshot + related lookups
  useEffect(() => {
    if (!taskId) { if(isMounted.current) {setError("Task ID missing."); setLoading(false);} return; }
    if (!currentUser) {
      if (currentUser === null && isMounted.current) {setError("Login required to view tasks."); setLoading(false);}
      return;
    }
    if (!userRole && isMounted.current) {
      setLoading(true); return;
    }

    if(isMounted.current) {
      setLoading(true); setError(''); setAssignees([]); setCreatorInfo({ name: '', photoURL: null }); setTask(null);
    }
    const taskRef = doc(db, 'tasks', taskId);

    const unsubscribeTask = onSnapshot(taskRef, async (taskSnap) => {
      if (!isMounted.current) return;
      if (!taskSnap.exists()) { setError('Task not found or you do not have access.'); setTask(null); setLoading(false); return; }

      const taskData = {
        id: taskSnap.id,
        ...taskSnap.data(),
        startDate: taskSnap.data().startDate?.toDate(),
        endDate: taskSnap.data().endDate?.toDate(),
        createdAt: taskSnap.data().createdAt?.toDate(),
        updatedAt: taskSnap.data().updatedAt?.toDate(),
        submittedAt: taskSnap.data().submittedAt?.toDate(),
        companyApprovedAt: taskSnap.data().companyApprovedAt?.toDate(),
        customerApprovedAt: taskSnap.data().customerApprovedAt?.toDate(),
        cancelledAt: taskSnap.data().cancelledAt?.toDate(),
        lastRejectedAt: taskSnap.data().lastRejectedAt?.toDate(),
        customerRejectedAt: taskSnap.data().customerRejectedAt?.toDate(),
        extensionRequest: taskSnap.data().extensionRequest ? {
          ...taskSnap.data().extensionRequest,
          requestedDate: taskSnap.data().extensionRequest.requestedDate?.toDate(),
          originalEndDate: taskSnap.data().extensionRequest.originalEndDate?.toDate(),
          respondedAt: taskSnap.data().extensionRequest.respondedAt?.toDate(),
          requestedAt: taskSnap.data().extensionRequest.requestedAt?.toDate(),
        } : null
      };
      const isManager = managerRolesForView.includes(userRole);
      const isCreator = taskData.createdBy === currentUser?.uid;
      const isAssigned = (taskData.assignedTo || []).includes(currentUser?.uid);
      const canView = isManager || isCreator || isAssigned;

      if (!canView) { setError("Access Denied: You do not have permission to view this task."); setTask(null); setLoading(false); return; }

      const previousTaskStatus = task?.status;
      setTask(taskData);
      if (taskData.status !== previousTaskStatus) { setNewStatus(taskData.status); }
      
      if (error.includes('load') || error.includes('found') || error.includes('Denied')) { setError(''); }

      if (isAssigned && taskData.createdBy !== currentUser.uid && !taskData.viewedBy?.[currentUser.uid]) {
        try { await updateDoc(taskRef, { [`viewedBy.${currentUser.uid}`]: serverTimestamp(), updatedAt: serverTimestamp() }); } catch (viewError) { console.error("Error recording view:", viewError); }
      }
      
      const currentAssignedToArray = taskData.assignedTo || [];
      const previousAssignedToArray = task?.assignedTo || [];
      const assigneesChanged = JSON.stringify(currentAssignedToArray.sort()) !== JSON.stringify(previousAssignedToArray.sort());

      if ((assignees.length === 0 || assigneesChanged) && currentAssignedToArray.length > 0) {
        try {
          const usersCol = collection(db, 'users');
          const limitedAssignedTo = currentAssignedToArray.slice(0, 30); 
          const qAssignees = query(usersCol, where('__name__', 'in', limitedAssignedTo));
          const usersSnap = await getDocs(qAssignees);
          if (!isMounted.current) return;
          const assigneeDetails = usersSnap.docs.map(d => ({ id: d.id, name: d.data().name || d.data().email, email: d.data().email, photoURL: d.data().photoURL || null }));
          setAssignees(assigneeDetails);
        } catch (assigneeErr) { console.error("Error fetching assignees:", assigneeErr); if(isMounted.current) setError(prev => `${prev ? prev + " | " : ""}Failed to load assignees.`); }
      } else if (currentAssignedToArray.length === 0) { 
        if(isMounted.current) setAssignees([]); 
      }

      if (taskData.createdBy && taskData.createdBy !== creatorInfo.id && taskData.createdBy !== task?.createdBy) {
        try {
          const creatorRef = doc(db, 'users', taskData.createdBy);
          const creatorSnap = await getDoc(creatorRef);
          if (!isMounted.current) return;
          setCreatorInfo(creatorSnap.exists() ? { id: creatorSnap.id, name: creatorSnap.data().name || creatorSnap.data().email || 'Unknown', photoURL: creatorSnap.data().photoURL || null } : { id: taskData.createdBy, name: 'Unknown User', photoURL: null });
        } catch (creatorErr) { console.error("Error fetching creator:", creatorErr); if(isMounted.current) setCreatorInfo({ id: taskData.createdBy, name: 'Error Loading Creator', photoURL: null }); }
      } else if (!taskData.createdBy && task?.createdBy) { if(isMounted.current) setCreatorInfo({ name: 'N/A', photoURL: null }); }

      if (loading) setLoading(false);
    }, (err) => {
      if (!isMounted.current) return;
      console.error("Error in task snapshot listener:", err);
      setError('Failed to load task details in real-time. Please refresh.'); setLoading(false);
    });
    return () => {
      unsubscribeTask();
    };
  }, [taskId, currentUser, userRole]);

  // Comments stream
  useEffect(() => {
    if (!taskId) return;
    if (!isMounted.current) return;

    setCommentsLoading(true); setCommentError('');
    const commentsRef = collection(db, 'tasks', taskId, 'comments');
    const q = query(commentsRef, orderBy('timestamp', 'asc'));
    const unsubscribeComments = onSnapshot(q, (querySnapshot) => {
      if (!isMounted.current) return;
      const fetchedComments = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data(), timestamp: doc.data().timestamp?.toDate() }));
      setComments(fetchedComments);
      setCommentsLoading(false);
    }, (err) => {
      if (!isMounted.current) return;
      console.error("Error fetching comments:", err);
      setCommentError("Failed to load comments."); setCommentsLoading(false);
    });
    return () => {
        unsubscribeComments();
    };
  }, [taskId]);

  // Activity log stream
  useEffect(() => {
    if (!taskId) return;
    if (!isMounted.current) return; 

    setActivityLoading(true);
    setActivityError('');
    const activityLogColRef = collection(db, 'tasks', taskId, 'activityLog');
    const qActivity = query(activityLogColRef, orderBy('timestamp', 'desc'));

    const unsubscribeActivity = onSnapshot(qActivity, (snapshot) => {
      if (!isMounted.current) return;
      const logData = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data(),
        timestamp: d.data().timestamp?.toDate() 
      }));
      setActivityLog(logData);
      setActivityLoading(false);
    }, (err) => {
      console.error("Error fetching activity log:", err);
      if(isMounted.current) {
        setActivityError("Failed to load task activity.");
        setActivityLoading(false);
      }
    });
    return () => {
        unsubscribeActivity();
    };
  }, [taskId]);

  // Utilities
  const findUserNameById = useCallback((userId) => {
    if (!userId || !allUsers || allUsers.length === 0) return userId || 'Unknown';
    const user = allUsers.find(u => u.id === userId);
    return user?.name || user?.email || userId.substring(0, 6); 
  }, [allUsers]);

  // --- ACTION HANDLERS ---
  const handleStatusChange = async () => {
    if (!task || newStatus === task.status || isProcessingAction) return;
    if (!statusUpdateRoles.includes(userRole)) {
      setError(`Your role (${userRole || 'N/A'}) cannot manually change status.`);
      setNewStatus(task.status); 
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => { if (isMounted.current) setError(''); }, 3000);
      return;
    }
    setUpdatingStatus(true); setError(''); 
    const oldStatus = task.status; 

    try {
      const taskRef = doc(db, 'tasks', taskId);
      const updateData = { status: newStatus, updatedAt: serverTimestamp() };
      
      const activityLogRef = doc(collection(db, 'tasks', taskId, 'activityLog'));
      const activityDescription = `${currentUser.displayName || currentUser.email} changed status from "${oldStatus}" to "${newStatus}".`;

      if (newStatus === 'Completed' && oldStatus !== 'Completed') updateData.completedAt = serverTimestamp();
      if (newStatus === 'Cancelled' && oldStatus !== 'Cancelled') updateData.cancelledAt = serverTimestamp();
      if (oldStatus === 'Completed' && newStatus !== 'Completed') updateData.completedAt = deleteField();
      if (oldStatus === 'Cancelled' && newStatus !== 'Cancelled') updateData.cancelledAt = deleteField();
      
      if (newStatus === 'In Progress') { 
        updateData.companyApprovedAt = deleteField(); 
        updateData.companyApprovedBy = deleteField();
        updateData.customerApprovedAt = deleteField(); 
        updateData.customerApprovedBy = deleteField();
        updateData.customerApprovalComment = deleteField();
        updateData.lastRejectionReason = deleteField();
        updateData.lastRejectedBy = deleteField();
        updateData.lastRejectedAt = deleteField();
        updateData.customerRejectionReason = deleteField();
        updateData.customerRejectedBy = deleteField();
        updateData.customerRejectedAt = deleteField();
      }
      
      const batch = writeBatch(db);
      batch.update(taskRef, updateData);
      batch.set(activityLogRef, {
        timestamp: serverTimestamp(),
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        actionType: 'status_changed',
        description: activityDescription,
        changes: [{ field: 'Status', from: oldStatus, to: newStatus }]
      });
      await batch.commit();

      setError(`Status updated to "${newStatus}"`); 
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => { if (isMounted.current) setError(''); }, 3000);
    } catch (err) {
      console.error("Error updating status: ", err); setError("Failed status update.");
      if (task && isMounted.current) setNewStatus(oldStatus); 
    } finally {
      if (isMounted.current) setUpdatingStatus(false);
    }
  };

  const handleDeleteTask = useCallback(async () => {
    if (!deleteTaskRoles.includes(userRole)) {
      setError("Permission denied to delete task.");
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => { if (isMounted.current) setError(''); }, 3000);
      return;
    }
    if (!task || isProcessingAction) return;
    if (window.confirm(`ARE YOU SURE you want to DELETE task "${task.taskName || 'this task'}"?\n\nThis action is IRREVERSIBLE and will delete the task, all comments, associated notifications, activity logs, and submitted files.`)) {
      setIsDeleting(true); setError('');
      try {
        const taskRef = doc(db, 'tasks', taskId);
        const batch = writeBatch(db);

        const commentsRef = collection(db, 'tasks', taskId, 'comments');
        const commentsSnap = await getDocs(commentsRef);
        commentsSnap.docs.forEach(commentDoc => batch.delete(commentDoc.ref));
        
        const activityLogRef = collection(db, 'tasks', taskId, 'activityLog');
        const activitySnap = await getDocs(activityLogRef);
        activitySnap.docs.forEach(activityDoc => batch.delete(activityDoc.ref));

        const storagePathsToDelete = new Set();
        (task.submissionFilesData || []).forEach(fileData => {
          if (fileData.path) storagePathsToDelete.add(fileData.path);
          else if (fileData.url) { try { storagePathsToDelete.add(ref(storage, fileData.url).fullPath); } catch(e){console.warn("Error parsing old file URL for deletion:", fileData.url, e)} }
        });
        if (storagePathsToDelete.size > 0) {
          const deleteFilePromises = Array.from(storagePathsToDelete).map(path => deleteObject(ref(storage, path)).catch(err => console.error(`Failed to delete storage file ${path}:`, err.code === 'storage/object-not-found' ? 'Not Found (OK)' : err)));
          await Promise.allSettled(deleteFilePromises);
        }

        const notificationsRef = collection(db, 'notifications');
        const qNotifications = query(notificationsRef, where('taskId', '==', taskId));
        const notificationsSnap = await getDocs(qNotifications);
        notificationsSnap.docs.forEach(notifDoc => batch.delete(notifDoc.ref));

        batch.delete(taskRef);
        await batch.commit();
        navigate('/dashboard', { replace: true, state: { message: `Task "${task.taskName}" deleted successfully.` }});
      } catch (err) {
        console.error("Error during task deletion process:", err);
        setError("Failed to delete task completely. Some associated data might remain.");
        if(isMounted.current) setIsDeleting(false);
      }
    }
  }, [taskId, task, userRole, navigate, isProcessingAction, storage]);

  const handleSendEmailNotification = () => {
    if (!task || assignees.length === 0 || isProcessingAction) return;
    setIsPreparingEmail(true); setError('');
    const validAssignees = assignees.filter(a => a.email);
    if (validAssignees.length === 0) { setError("No valid assignee emails found."); setIsPreparingEmail(false); return; }
    const recipientEmails = validAssignees.map(a => a.email).join(',');
    const subject = `Task Info: ${task.taskName || 'Untitled Task'}`;
    const bodyLines = [
      `Hello,`, ``, `Please find the details for the following task:`, ``,
      `   Task Name: "${task.taskName || 'Untitled'}"`,
      `   Description: ${task.description || '(No description provided)'}`,
      `   Status: ${task.status || 'N/A'}`, `   Priority: ${task.priority || 'Medium'}`,
      `   Start Date: ${task.startDate ? formatDate(task.startDate) : 'Not set'}`,
      `   End Date: ${task.endDate ? formatDate(task.endDate) : 'Not set'}`, ``,
      `You can view the full task details here:`, `${window.location.origin}/task/${taskId}`, ``,
      `Thanks,`, `${currentUser?.displayName || currentUser?.email || 'Management'}`
    ];
    const body = bodyLines.join('\n');
    const mailtoLink = `mailto:${recipientEmails}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    try {
      const emailWindow = window.open(mailtoLink, '_blank');
      if (!emailWindow && !navigator.userAgent.match(/mobile/i)) {
        throw new Error("Popup blocked or mailto not supported?");
      }
      setError("Opening email client...");
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => { if (isMounted.current) setError(''); }, 2000);
    } catch (err) {
      console.warn("Mailto link failed:", err); setError("Could not open email client automatically. Please check popup blockers.");
    } finally {
      setTimeout(() => { if(isMounted.current) setIsPreparingEmail(false); }, 1000);
    }
  };

  const handleAcknowledgeTask = useCallback(async () => {
    if (!task || !currentUser || isProcessingAction || task.acknowledgedBy?.[currentUser.uid]) return;
    setIsAcknowledging(true); setError('');
    const taskRef = doc(db, 'tasks', taskId);
    const batch = writeBatch(db);
    try {
      batch.update(taskRef, {
        [`acknowledgedBy.${currentUser.uid}`]: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      const activityLogRef = doc(collection(db, 'tasks', taskId, 'activityLog'));
      batch.set(activityLogRef, {
        timestamp: serverTimestamp(),
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        actionType: 'task_acknowledged',
        description: `${currentUser.displayName || currentUser.email} acknowledged the task.`
      });
      await batch.commit();

      setError("Task acknowledged!");
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => { if (isMounted.current) setError(''); }, 3000);
    } catch (err) {
      console.error("Error acknowledging task:", err);
      setError("Failed to acknowledge task.");
    } finally {
      if (isMounted.current) setIsAcknowledging(false);
    }
  }, [taskId, task, currentUser, isProcessingAction]);

  // Submission form handlers
  const handleSubmissionTextChange = (e) => {
    const newText = e.target.value;
    setSubmissionText(newText);
    if (newText.trim()) {
      if (linkInputs.some(link => link.trim()) || submissionFiles.length > 0) {
        setLinkInputs(['']);
        filePreviews.forEach(p => { if (p.url && p.url.startsWith('blob:')) URL.revokeObjectURL(p.url); });
        setSubmissionFiles([]);
        setFilePreviews([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setSubmitError("Links and files cleared as text is being entered. Submit one type only.");
      } else {
        if (submitError && !submitError.startsWith("Links and files cleared")) setSubmitError('');
        else if (submitError.startsWith("Links and files cleared")) setSubmitError('');
      }
    } else {
      if (submitError.includes("Links and files cleared")) setSubmitError('');
    }
  };
  
  const handleLinkInputChange = (index, value) => {
    const newLinks = [...linkInputs];
    newLinks[index] = value;
    setLinkInputs(newLinks);
    const anyLinkHasValue = newLinks.some(link => link.trim());
    if (anyLinkHasValue) {
      if (submissionText.trim() || submissionFiles.length > 0) {
        setSubmissionText('');
        filePreviews.forEach(p => { if (p.url && p.url.startsWith('blob:')) URL.revokeObjectURL(p.url); });
        setSubmissionFiles([]);
        setFilePreviews([]);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setSubmitError("Text and files cleared as links are being entered. Submit one type only.");
      } else {
        if (submitError && !submitError.startsWith("Text and files cleared")) setSubmitError('');
        else if (submitError.startsWith("Text and files cleared")) setSubmitError('');
      }
    } else {
      if (submitError.includes("Text and files cleared")) setSubmitError('');
    }
  };

  const handleFileChange = (e) => {
    setSubmitError('');
    const files = Array.from(e.target.files);
    const currentFileCount = submissionFiles.length;
    const remainingSlots = MAX_UPLOAD_COUNT - currentFileCount;

    if (files.length === 0) return;

    if (files.length > remainingSlots) {
      setSubmitError(`You can only upload ${remainingSlots} more file(s) (Max ${MAX_UPLOAD_COUNT} total).`);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }

    const newlyAddedFiles = [];
    const newlyAddedPreviews = [];
    let sizeError = false;
    let typeError = false;
    let duplicateError = false;
    const existingFileNames = new Set(submissionFiles.map(f => f.name));
    
    files.forEach(file => {
      if (existingFileNames.has(file.name)) { duplicateError = true; return; }
      const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'application/pdf', 'application/zip', 'application/x-rar-compressed', 'application/vnd.rar', 'application/x-7z-compressed'];
      if (!allowedTypes.includes(file.type)) { typeError = true; return; }
      if (file.size > MAX_UPLOAD_SIZE_MB * 1024 * 1024) { sizeError = true; return; }
      newlyAddedFiles.push(file);
      newlyAddedPreviews.push({ name: file.name, size: file.size, type: file.type, url: file.type.startsWith('image/') ? URL.createObjectURL(file) : null });
      existingFileNames.add(file.name);
    });

    let errorMessages = [];
    if (duplicateError) errorMessages.push("Duplicate file names were skipped.");
    if (typeError) errorMessages.push("Invalid file types were skipped (Allowed: images, pdf, zip, rar, 7z).");
    if (sizeError) errorMessages.push(`Files larger than ${MAX_UPLOAD_SIZE_MB}MB were skipped.`);
    
    if (newlyAddedFiles.length > 0) {
      if (submissionText.trim() || linkInputs.some(link => link.trim())) {
        setSubmissionText('');
        setLinkInputs(['']);
        if (errorMessages.length === 0) {
          errorMessages.push("Text and links cleared as files are being selected. Submit one type only.");
        } else {
          errorMessages.push("Additionally, text and links were cleared.");
        }
      }
      
      const combinedFiles = [...submissionFiles, ...newlyAddedFiles].slice(0, MAX_UPLOAD_COUNT);
      const combinedPreviews = [...filePreviews, ...newlyAddedPreviews].slice(0, MAX_UPLOAD_COUNT);
      
      filePreviews.forEach(p => { if (p.url && p.url.startsWith('blob:') && !combinedPreviews.find(cp => cp.url === p.url) ) URL.revokeObjectURL(p.url); });

      setSubmissionFiles(combinedFiles);
      setFilePreviews(combinedPreviews);
    }

    if (errorMessages.length > 0) setSubmitError(errorMessages.join(' '));

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeFile = (indexToRemove) => {
    if (filePreviews[indexToRemove]?.url && filePreviews[indexToRemove].url.startsWith('blob:')) {
      URL.revokeObjectURL(filePreviews[indexToRemove].url);
    }
    setSubmissionFiles(prev => prev.filter((_, index) => index !== indexToRemove));
    setFilePreviews(prev => prev.filter((_, index) => index !== indexToRemove));
    setSubmitError(''); 
  };

  const addLinkInput = () => {
    if (linkInputs.length < 10) {
      setLinkInputs([...linkInputs, '']);
    } else {
      setSubmitError("Maximum 10 links allowed.");
      setTimeout(() => {if(isMounted.current) setSubmitError('')}, 3000);
    }
  };
  const removeLinkInput = (index) => {
    if (linkInputs.length <= 1) { 
      handleLinkInputChange(0, '');
      return;
    }
    setLinkInputs(prev => prev.filter((_, i) => i !== index));
    setSubmitError(''); 
  };

  const handleSubmitWork = async (e) => {
    e.preventDefault();
    setSubmitError(''); setSubmitSuccess('');

    const trimmedSubmissionText = submissionText.trim();
    const validLinks = linkInputs.filter(link => link.trim());
    const filesProvided = submissionFiles.length > 0;

    const textProvided = trimmedSubmissionText.length > 0;
    const linksProvided = validLinks.length > 0;

    const submissionOptionsCount = (textProvided ? 1 : 0) + (linksProvided ? 1 : 0) + (filesProvided ? 1 : 0);

    if (submissionOptionsCount === 0) {
      setSubmitError("Please provide a submission: either text notes, links, or files.");
      return;
    }
    if (submissionOptionsCount > 1) {
      setSubmitError("Please submit only one type of content (text, links, or files), not a combination.");
      return;
    }

    if (linksProvided) { for (const link of validLinks) { try { new URL(link); } catch (_) { setSubmitError(`Invalid URL format: "${link.substring(0, 30)}..."`); return; } } }
    if (filesProvided && submissionFiles.length > MAX_UPLOAD_COUNT) { setSubmitError(`You can upload a maximum of ${MAX_UPLOAD_COUNT} files.`); return; }
    if (linksProvided && validLinks.length > 10) { setSubmitError(`You can add a maximum of 10 links.`); return; }

    if (!task || !currentUser) { setSubmitError("Critical error: Task or user data is missing."); return; }
    
    const isCurrentUserAssigned = (task.assignedTo || []).includes(currentUser.uid);
    const isCurrentUserCreator = task.createdBy === currentUser.uid;

    if (!isCurrentUserAssigned && !isCurrentUserCreator) { 
      setSubmitError("You are not an assignee nor the creator of this task, so you cannot submit work."); 
      return; 
    }

    if (['Completed', 'Cancelled', 'Company Approval', 'Customer Approval'].includes(task.status)) { setSubmitError(`Cannot submit work when task status is "${task.status}".`); return; }

    setIsSubmittingWork(true);
    setIsUploadingFile(filesProvided);

    const taskRef = doc(db, 'tasks', taskId);
    const batch = writeBatch(db);
    let uploadedFileData = [];

    const previousPathsToDelete = new Set();
    if (task.submissionFilesData) { 
      task.submissionFilesData.forEach(fileData => {
        if (fileData.path) previousPathsToDelete.add(fileData.path);
        else if (fileData.url) { try { previousPathsToDelete.add(ref(storage, fileData.url).fullPath); } catch(e){console.warn("Error parsing old file URL for deletion:", fileData.url, e)} }
      });
    }
    
    let submissionType = '';
    if(textProvided) submissionType = 'text notes';
    else if(linksProvided) submissionType = linksProvided.length > 1 ? 'links' : 'a link';
    else if(filesProvided) submissionType = submissionFiles.length > 1 ? 'files' : 'a file';

    try {
      if (filesProvided) {
        const uploadPromises = submissionFiles.map(file => {
          const filePath = `taskSubmissions/${taskId}/${currentUser.uid}/${Date.now()}_${file.name}`;
          const fileStorageRef = ref(storage, filePath);
          return uploadBytes(fileStorageRef, file).then(uploadResult =>
            getDownloadURL(uploadResult.ref).then(url => ({
              url, name: file.name, type: file.type, size: file.size, path: filePath
            }))
          );
        });
        uploadedFileData = await Promise.all(uploadPromises);
        if(isMounted.current) setIsUploadingFile(false);
      }

      const taskUpdateData = {
        status: 'Company Approval',
        submittedAt: serverTimestamp(),
        submittedBy: currentUser.uid,
        updatedAt: serverTimestamp(),
        submissionTextData: deleteField(), 
        submissionLinks: deleteField(),
        submissionFilesData: deleteField(),
        lastRejectionReason: deleteField(), lastRejectedBy: deleteField(), lastRejectedAt: deleteField(),
        customerRejectionReason: deleteField(), customerRejectedBy: deleteField(), customerRejectedAt: deleteField(),
      };

      if (textProvided) {
        taskUpdateData.submissionTextData = trimmedSubmissionText;
      } else if (linksProvided) {
        taskUpdateData.submissionLinks = validLinks;
      } else if (filesProvided) {
        taskUpdateData.submissionFilesData = uploadedFileData;
      }
      batch.update(taskRef, taskUpdateData);

      const activityLogRef = doc(collection(db, 'tasks', taskId, 'activityLog'));
      const activityDescription = `${currentUser.displayName || currentUser.email} submitted work (${submissionType}) for approval.`;
      batch.set(activityLogRef, {
        timestamp: serverTimestamp(),
        userId: currentUser.uid,
        userName: currentUser.displayName || currentUser.email,
        actionType: 'work_submitted',
        description: activityDescription
      });

      const userIdsToNotify = new Set(); const notifiedUserIds = new Set();
      if (task.createdBy && task.createdBy !== currentUser.uid) userIdsToNotify.add(task.createdBy);
      allUsers.forEach(user => { if (companyApprovalRoles.includes(user.role) && user.id !== currentUser.uid && !userIdsToNotify.has(user.id)) { userIdsToNotify.add(user.id); } });

      if (userIdsToNotify.size > 0) {
        const notificationsCol = collection(db, 'notifications');
        const submitterName = currentUser.displayName || currentUser.email || 'Assignee';
        const message = `${submitterName} submitted work for task: "${task.taskName || 'Untitled Task'}" (Awaiting Company Approval)`;
        userIdsToNotify.forEach(notifyUserId => { if (!notifiedUserIds.has(notifyUserId)) { const notificationRef = doc(notificationsCol); batch.set(notificationRef, { userId: notifyUserId, taskId: taskId, taskName: task.taskName || 'Untitled Task', message: message, timestamp: serverTimestamp(), isRead: false, createdBy: currentUser.uid, createdByName: submitterName, type: "task_submission" }); notifiedUserIds.add(notifyUserId); } });
      }
      await batch.commit();

      if (previousPathsToDelete.size > 0) {
        const deletePromises = Array.from(previousPathsToDelete).map(path => deleteObject(ref(storage, path)).catch(err => console.error(`Failed to delete old storage file ${path}:`, err.code === 'storage/object-not-found' ? 'Not Found (OK)' : err)));
        await Promise.allSettled(deletePromises);
      }

      setSubmitSuccess("Work submitted successfully for approval!");
      setSubmissionText(''); 
      setLinkInputs(['']); 
      setSubmissionFiles([]);
      filePreviews.forEach(p => { if (p.url && p.url.startsWith('blob:')) URL.revokeObjectURL(p.url); }); 
      setFilePreviews([]);
      if (fileInputRef.current) fileInputRef.current.value = ''; 
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => { if (isMounted.current) setSubmitSuccess(''); }, 4000);

    } catch (err) {
      console.error("Error submitting work:", err);
      if (err.code?.startsWith('storage/')) {
        setSubmitError(`File upload failed: ${err.message}. Please try again.`);
      } else {
        setSubmitError("Failed to submit work. An unexpected error occurred. Please try again.");
      }
      if(isMounted.current) setIsUploadingFile(false); 
    } finally {
      if (isMounted.current) {
        setIsSubmittingWork(false);
        setIsUploadingFile(false); 
      }
    }
  };

  // Extension rejection (from modal)
  const submitExtensionRejection = async (reason, newDateStr) => {
    if (!task || !task.extensionRequest || task.extensionRequest.status !== 'pending' || isProcessingAction || !currentUser) {
      if(isMounted.current) setShowExtensionRejectionModal(false); return;
    }
    if (!reason || !reason.trim()) {
      if(isMounted.current) setError("Rejection reason is required."); 
      return;
    }
    if(isMounted.current) setIsRespondingToExtension(true); setError('');

    const taskRef = doc(db, 'tasks', taskId);
    const batch = writeBatch(db);
    const responderName = currentUser.displayName || currentUser.email || 'Manager';
    let newEndDateTimestamp = null;
    let dateUpdateApplied = false;
    let endDateForLog = task.endDate ? formatDate(task.endDate) : 'Not set';
    
    const originalEndDateForComparison = task.extensionRequest.originalEndDate?.toDate() || task.endDate || new Date(0);

    if (newDateStr) {
      try {
        const parsedDate = new Date(newDateStr + "T00:00:00");
        if (!isNaN(parsedDate) && parsedDate.getTime() > new Date(originalEndDateForComparison).getTime()) {
          newEndDateTimestamp = Timestamp.fromDate(parsedDate);
          dateUpdateApplied = true;
          endDateForLog = formatDate(parsedDate);
        } else {
          console.warn("Invalid new end date during rejection or not after original. Date will not be changed.");
        }
      } catch (dateParseError) {
        console.error("Error parsing new date for extension rejection:", dateParseError);
      }
    }

    try {
      const updateData = {
        [`extensionRequest.status`]: 'rejected',
        [`extensionRequest.managerComment`]: reason.trim(),
        [`extensionRequest.respondedBy`]: currentUser.uid,
        [`extensionRequest.respondedByName`]: responderName,
        [`extensionRequest.respondedAt`]: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      if (dateUpdateApplied && newEndDateTimestamp) {
        updateData.endDate = newEndDateTimestamp; 
      }
      batch.update(taskRef, updateData);

      const activityLogRef = doc(collection(db, 'tasks', taskId, 'activityLog'));
      let activityDescription = `${responderName} rejected the extension request. Reason: ${reason.trim()}.`;
      const changes = [{field: 'Extension Request Status', from: 'pending', to: 'rejected'}];
      if (dateUpdateApplied && newEndDateTimestamp) {
        activityDescription += ` End date was updated to ${endDateForLog}.`;
        changes.push({field: 'End Date', from: formatDate(task.endDate), to: endDateForLog});
      }
      batch.set(activityLogRef, {
        timestamp: serverTimestamp(), userId: currentUser.uid, userName: responderName,
        actionType: 'extension_rejected', description: activityDescription, changes
      });

      const requesterId = task.extensionRequest.requestedBy;
      if (requesterId && requesterId !== currentUser.uid) { 
        const notificationsCol = collection(db, 'notifications');
        let message = `Your extension request for task "${task.taskName || 'Untitled Task'}" was rejected by ${responderName}. Reason: ${reason.trim()}.`;
        const originalDateForMessage = task.extensionRequest.originalEndDate?.toDate() || task.endDate;
        if (dateUpdateApplied && newEndDateTimestamp) {
          message += ` The end date has been set to: ${endDateForLog}.`;
        } else {
          message += ` The original end date remains: ${originalDateForMessage ? formatDate(originalDateForMessage) : 'Not Set'}.`;
        }
        const notificationRef = doc(notificationsCol);
        batch.set(notificationRef, {
          userId: requesterId, taskId: taskId, taskName: task.taskName || 'Untitled Task',
          message: message, timestamp: serverTimestamp(), isRead: false,
          createdBy: currentUser.uid, createdByName: responderName, type: "task_extension_rejected"
        });
      }
      await batch.commit();
      if(isMounted.current){
        let feedbackMsg = "Extension request rejected.";
        if (dateUpdateApplied && newEndDateTimestamp) feedbackMsg += " Task end date updated as specified.";
        else if (newDateStr && !dateUpdateApplied) feedbackMsg += " Specified new end date was invalid; original end date retained.";
        setError(feedbackMsg); 
        setShowExtensionRejectionModal(false);
        if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = setTimeout(() => { if (isMounted.current) setError(''); }, 4000);
      }
    } catch (err) {
      console.error("Error rejecting extension:", err);
      if(isMounted.current) setError("Failed to reject extension request.");
    } finally {
      if (isMounted.current) setIsRespondingToExtension(false);
    }
  };

  const handleRejectExtension = () => {
    if (!task || !task.extensionRequest || task.extensionRequest.status !== 'pending' || isProcessingAction) return;
    if (!extensionResponseRoles.includes(userRole)) { 
      if(isMounted.current) setError("Permission denied to respond to extension requests."); 
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => {if(isMounted.current) setError('')}, 3000); 
      return; 
    }
    if(isMounted.current) {setShowExtensionRejectionModal(true); setError('');}
  };

  const handleRequestExtension = async (e) => {
    e.preventDefault(); 
    if (!isMounted.current) return;
    setExtensionRequestError('');
    if (!task || !currentUser || isProcessingAction) return;
    if (!requestedEndDate) { setExtensionRequestError("Please select a new end date."); return; }
    if (!extensionRequestReason.trim()) { setExtensionRequestError("Please provide a reason for the extension."); return; }
    
    const newEndDate = new Date(requestedEndDate + "T00:00:00");
    const currentEndDate = task.endDate || new Date(0); 
    
    if (newEndDate <= currentEndDate) { setExtensionRequestError("New end date must be after the current end date."); return; }
    if (task.extensionRequest && task.extensionRequest.status !== 'rejected') {
      setExtensionRequestError(`An extension request already exists with status: ${task.extensionRequest.status}.`);
      return;
    }
    if (['Completed', 'Cancelled'].includes(task.status)) { setExtensionRequestError(`Cannot request extension for a ${task.status} task.`); return; }

    setIsSubmittingExtensionRequest(true);
    const taskRef = doc(db, 'tasks', taskId);
    const batch = writeBatch(db);
    const requesterName = currentUser.displayName || currentUser.email || 'Assignee';
    const requestData = {
      requestedBy: currentUser.uid,
      requestedByName: requesterName,
      requestedDate: Timestamp.fromDate(newEndDate), 
      originalEndDate: task.endDate ? Timestamp.fromDate(task.endDate) : null, 
      reason: extensionRequestReason.trim(),
      status: 'pending', 
      requestedAt: serverTimestamp(),
      respondedBy: null, respondedAt: null, managerComment: null
    };
    try {
      batch.update(taskRef, { extensionRequest: requestData, updatedAt: serverTimestamp() });
      
      const activityLogRef = doc(collection(db, 'tasks', taskId, 'activityLog'));
      const activityDescription = `${requesterName} requested an extension to ${formatDate(newEndDate)}. Reason: ${requestData.reason}`;
      batch.set(activityLogRef, {
        timestamp: serverTimestamp(), userId: currentUser.uid, userName: requesterName,
        actionType: 'extension_requested', description: activityDescription,
        changes: [{field: 'Requested End Date', to: formatDate(newEndDate)}]
      });

      const userIdsToNotify = new Set(); const notifiedUserIds = new Set();
      if (task.createdBy && task.createdBy !== currentUser.uid) userIdsToNotify.add(task.createdBy);
      allUsers.forEach(user => { if (extensionResponseRoles.includes(user.role) && user.id !== currentUser.uid && !userIdsToNotify.has(user.id)) userIdsToNotify.add(user.id); });
      if (userIdsToNotify.size > 0) { 
        const notificationsCol = collection(db, 'notifications'); 
        const message = `${requesterName} requested an extension for task: "${task.taskName || 'Untitled Task'}" to ${formatDate(newEndDate)}. Reason: ${requestData.reason}`; 
        userIdsToNotify.forEach(notifyUserId => { if (!notifiedUserIds.has(notifyUserId)) { 
          const notificationRef = doc(notificationsCol); 
          batch.set(notificationRef, { userId: notifyUserId, taskId: taskId, taskName: task.taskName || 'Untitled Task', message: message, timestamp: serverTimestamp(), isRead: false, createdBy: currentUser.uid, createdByName: requesterName, type: "task_extension_request" }); 
          notifiedUserIds.add(notifyUserId); 
        }}); 
      }
      await batch.commit();
      if (isMounted.current) {
        setShowExtensionRequestForm(false); setRequestedEndDate(''); setExtensionRequestReason('');
        setError("Extension request submitted successfully."); 
        if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = setTimeout(() => { if (isMounted.current) setError(''); }, 3000);
      }
    } catch (err) {
      console.error("Error submitting extension request:", err);
      if (isMounted.current) setExtensionRequestError("Failed to submit extension request. Please try again.");
    } finally {
      if (isMounted.current) setIsSubmittingExtensionRequest(false);
    }
  };

  // Customer approval handlers
  const handleCustomerApprove = async () => {
    if (!task || task.status !== 'Customer Approval' || isProcessingAction || !currentUser) return;
    if (!customerApprovalRoles.includes(userRole)) {
      if(isMounted.current) setCustomerApprovalError("Permission Denied.");
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => {if(isMounted.current) setCustomerApprovalError('')}, 3000);
      return;
    }
    
    const approvalComment = window.prompt("Optional: Add customer feedback or approval notes (e.g., PO number, approval reference). Click OK if none.");
    
    if(isMounted.current) {
      setIsCustomerApproving(true);
      setCustomerApprovalError('');
      setCustomerApprovalSuccess('');
      setError('');
    }

    const taskRef = doc(db, 'tasks', taskId);
    const batch = writeBatch(db);
    const approverName = currentUser.displayName || currentUser.email || 'Admin';

    try {
      const updatePayload = {
        status: 'Completed',
        completedAt: serverTimestamp(),
        customerApprovedAt: serverTimestamp(),
        customerApprovedBy: currentUser.uid,
        updatedAt: serverTimestamp()
      };
      if (approvalComment && approvalComment.trim()) {
        updatePayload.customerApprovalComment = approvalComment.trim();
      }
      batch.update(taskRef, updatePayload);

      if (approvalComment && approvalComment.trim()) {
        const commentRef = doc(collection(db, 'tasks', taskId, 'comments'));
        batch.set(commentRef, {
          text: `Customer Feedback/Approval Note: ${approvalComment.trim()}`,
          timestamp: serverTimestamp(),
          userId: currentUser.uid,
          userName: approverName,
          userPhotoURL: currentUser.photoURL || null,
          type: 'customer_approval'
        });
      }

      const activityLogRef = doc(collection(db, 'tasks', taskId, 'activityLog'));
      let activityDescription = `${approverName} confirmed customer approval. Task Completed!`;
      if (approvalComment && approvalComment.trim()) {
        activityDescription += ` Feedback: ${approvalComment.trim()}`;
      }
      batch.set(activityLogRef, {
        timestamp: serverTimestamp(),
        userId: currentUser.uid,
        userName: approverName,
        actionType: 'task_customer_approved',
        description: activityDescription,
        changes: [{ field: 'Status', from: 'Customer Approval', to: 'Completed' }]
      });

      const userIdsToNotify = new Set();
      const notifiedUserIds = new Set();
      if (task.createdBy && task.createdBy !== currentUser.uid) userIdsToNotify.add(task.createdBy);
      (task.assignedTo || []).forEach(uid => { if (uid !== currentUser.uid) userIdsToNotify.add(uid); });
      if (task.companyApprovedBy && task.companyApprovedBy !== currentUser.uid && !userIdsToNotify.has(task.companyApprovedBy)) userIdsToNotify.add(task.companyApprovedBy);

      if (userIdsToNotify.size > 0) {
        const notificationsCol = collection(db, 'notifications');
        const message = `Task "${task.taskName || 'Untitled Task'}" was approved by the customer and is now Completed!`;
        userIdsToNotify.forEach(notifyUserId => {
          if (!notifiedUserIds.has(notifyUserId)) {
            const notificationRef = doc(notificationsCol);
            batch.set(notificationRef, {
              userId: notifyUserId, taskId: taskId, taskName: task.taskName || 'Untitled Task',
              message: message, timestamp: serverTimestamp(), isRead: false,
              createdBy: currentUser.uid, createdByName: approverName, type: "task_customer_approved"
            });
            notifiedUserIds.add(notifyUserId);
          }
        });
      }

      await batch.commit();

      if (isMounted.current) {
        setCustomerApprovalSuccess("Task approved by customer and marked as Completed!");
        if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = setTimeout(() => { if (isMounted.current) setCustomerApprovalSuccess(''); }, 4000);
      }

      // >>> NEW: After marking Completed, offer to update a client goal
      openPromptAfterCustomerApprove();

    } catch (err) {
      console.error("Error during customer approval:", err);
      if (isMounted.current) setCustomerApprovalError("Failed to complete task approval by customer.");
    } finally {
      if (isMounted.current) setIsCustomerApproving(false);
    }
  };

  const handleCustomerReject = () => {
    if (!task || task.status !== 'Customer Approval' || isProcessingAction) return;
    if (!customerApprovalRoles.includes(userRole)) {
      if(isMounted.current) setCustomerApprovalError("Permission Denied.");
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => {if(isMounted.current) setCustomerApprovalError('')}, 3000);
      return;
    }
    if(isMounted.current) {
      setShowCustomerRejectionModal(true);
      setCustomerApprovalError('');
      setCustomerApprovalSuccess('');
    }
  };

  // Company rejection/approval
  const submitRejection = async (reason) => {
    if (!reason || !task || task.status !== 'Company Approval' || isProcessingAction || !currentUser) {
      if (isMounted.current) setShowRejectionModal(false);
      return;
    }
    if(isMounted.current) {
      setIsRejecting(true);
      setApprovalError('');
      setApprovalSuccess('');
      setError('');
    }

    const batch = writeBatch(db);
    const taskRef = doc(db, 'tasks', taskId);
    const rejectorName = currentUser.displayName || currentUser.email || 'Admin';
    const commentText = `Reason for Company Rejection: ${reason}`;

    try {
      const commentRef = doc(collection(db, 'tasks', taskId, 'comments'));
      batch.set(commentRef, {
        text: commentText,
        timestamp: serverTimestamp(),
        userId: currentUser.uid,
        userName: rejectorName,
        userPhotoURL: currentUser.photoURL || null,
        type: 'rejection'
      });

      batch.update(taskRef, {
        status: 'In Progress',
        updatedAt: serverTimestamp(),
        lastRejectionReason: reason.trim(),
        lastRejectedBy: currentUser.uid,
        lastRejectedAt: serverTimestamp(),
        companyApprovedAt: deleteField(),
        companyApprovedBy: deleteField()
      });

      const activityLogRef = doc(collection(db, 'tasks', taskId, 'activityLog'));
      const activityDescription = `${rejectorName} rejected the task (company level). Status changed to In Progress. Reason: ${reason.trim()}`;
      batch.set(activityLogRef, {
        timestamp: serverTimestamp(),
        userId: currentUser.uid,
        userName: rejectorName,
        actionType: 'task_company_rejected',
        description: activityDescription,
        changes: [{ field: 'Status', from: 'Company Approval', to: 'In Progress' }],
        commentId: commentRef.id
      });

      const userIdsToNotify = new Set();
      const notifiedUserIds = new Set();
      if (task.createdBy && task.createdBy !== currentUser.uid) userIdsToNotify.add(task.createdBy);
      (task.assignedTo || []).forEach(uid => userIdsToNotify.add(uid));

      if (userIdsToNotify.size > 0) {
        const notificationsCol = collection(db, 'notifications');
        const message = `Work for task "${task.taskName || 'Untitled Task'}" was rejected by ${rejectorName} and requires rework. Reason: ${reason.trim()}`;
        userIdsToNotify.forEach(notifyUserId => {
          if (!notifiedUserIds.has(notifyUserId)) {
            const notificationRef = doc(notificationsCol);
            batch.set(notificationRef, {
              userId: notifyUserId, taskId: taskId, taskName: task.taskName || 'Untitled Task',
              message: message, timestamp: serverTimestamp(), isRead: false,
              createdBy: currentUser.uid, createdByName: rejectorName, type: "task_rejected"
            });
            notifiedUserIds.add(notifyUserId);
          }
        });
      }

      await batch.commit();

      if (isMounted.current) {
        setApprovalSuccess("Task rejected and returned to 'In Progress'.");
        setShowRejectionModal(false);
        if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = setTimeout(() => { if (isMounted.current) setApprovalSuccess(''); }, 4000);
      }
    } catch (err) {
      console.error("Error rejecting task:", err);
      if (isMounted.current) setApprovalError("Failed to reject task. Please try again.");
    } finally {
      if (isMounted.current) setIsRejecting(false);
    }
  };

  const handleReject = () => {
    if (!task || task.status !== 'Company Approval' || isProcessingAction) return;
    if (!companyApprovalRoles.includes(userRole)) {
      if(isMounted.current) setApprovalError("Permission Denied to reject this task.");
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => {if(isMounted.current) setApprovalError('')}, 3000);
      return;
    }
    if(isMounted.current) {
      setShowRejectionModal(true);
      setApprovalError('');
      setApprovalSuccess('');
    }
  };

  const handleApprove = async () => {
    if (!task || task.status !== 'Company Approval' || isProcessingAction || !currentUser) return;
    if (!companyApprovalRoles.includes(userRole)) {
      if (isMounted.current) setError("Permission Denied to approve this task.");
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => { if (isMounted.current) setError(''); }, 3000);
      return;
    }

    if (isMounted.current) {
      setIsApproving(true);
      setApprovalError('');
      setApprovalSuccess('');
      setError('');
    }

    const taskRef = doc(db, 'tasks', taskId);
    const batch = writeBatch(db);
    const approverName = currentUser.displayName || currentUser.email || 'System';

    try {
      batch.update(taskRef, {
        status: 'Customer Approval',
        companyApprovedAt: serverTimestamp(),
        companyApprovedBy: currentUser.uid,
        updatedAt: serverTimestamp(),
        lastRejectionReason: deleteField(),
        lastRejectedBy: deleteField(),
        lastRejectedAt: deleteField(),
      });

      const activityLogRef = doc(collection(db, 'tasks', taskId, 'activityLog'));
      const activityDescription = `${approverName} approved the task internally. Status changed to Customer Approval.`;
      batch.set(activityLogRef, {
        timestamp: serverTimestamp(),
        userId: currentUser.uid,
        userName: approverName,
        actionType: 'task_company_approved',
        description: activityDescription,
        changes: [{ field: 'Status', from: 'Company Approval', to: 'Customer Approval' }]
      });

      const userIdsToNotify = new Set();
      const notifiedUserIds = new Set();
      if (task.createdBy && task.createdBy !== currentUser.uid) userIdsToNotify.add(task.createdBy);
      (task.assignedTo || []).forEach(uid => { if (uid !== currentUser.uid) userIdsToNotify.add(uid); });
      allUsers.forEach(user => {
        if (customerApprovalRoles.includes(user.role) && user.id !== currentUser.uid && !userIdsToNotify.has(user.id)) {
          userIdsToNotify.add(user.id);
        }
      });

      if (userIdsToNotify.size > 0) {
        const notificationsCol = collection(db, 'notifications');
        const message = `Task "${task.taskName || 'Untitled'}" was approved internally by ${approverName} and is now awaiting Customer Approval.`;
        userIdsToNotify.forEach(notifyUserId => {
          if (!notifiedUserIds.has(notifyUserId)) {
            const notificationRef = doc(notificationsCol);
            batch.set(notificationRef, {
              userId: notifyUserId, taskId: taskId, taskName: task.taskName || 'Untitled Task',
              message: message, timestamp: serverTimestamp(), isRead: false,
              createdBy: currentUser.uid, createdByName: approverName, type: "task_company_approved"
            });
            notifiedUserIds.add(notifyUserId);
          }
        });
      }

      await batch.commit();

      if (isMounted.current) {
        setApprovalSuccess("Task approved internally. Moved to Customer Approval.");
        if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = setTimeout(() => { if (isMounted.current) setApprovalSuccess(''); }, 4000);
      }
    } catch (err) {
      console.error("Error approving task internally:", err);
      if (isMounted.current) setApprovalError("Failed to approve task. Please try again.");
    } finally {
      if (isMounted.current) setIsApproving(false);
    }
  };

  // Extension approve
  const handleApproveExtension = async () => {
    if (!task || !task.extensionRequest || task.extensionRequest.status !== 'pending' || isProcessingAction || !currentUser) return;
    if (!extensionResponseRoles.includes(userRole)) { 
      if(isMounted.current) setError("Permission denied to respond to extension requests."); 
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => {if(isMounted.current) setError('')}, 3000); 
      return; 
    }
    if(isMounted.current) setIsRespondingToExtension(true); setError('');
    const taskRef = doc(db, 'tasks', taskId);
    const batch = writeBatch(db);
    const responderName = currentUser.displayName || currentUser.email || 'Manager';
    
    const newEndDate = task.extensionRequest.requestedDate; 
    if (!(newEndDate instanceof Date) || isNaN(newEndDate.getTime())) {
      console.error("Invalid requestedDate in extensionRequest:", task.extensionRequest.requestedDate);
      if(isMounted.current) setError("Invalid requested extension date. Cannot approve.");
      if(isMounted.current) setIsRespondingToExtension(false);
      return;
    }

    try {
      const newEndDateTimestamp = Timestamp.fromDate(newEndDate);

      batch.update(taskRef, {
        endDate: newEndDateTimestamp,
        [`extensionRequest.status`]: 'approved',
        [`extensionRequest.respondedBy`]: currentUser.uid,
        [`extensionRequest.respondedByName`]: responderName,
        [`extensionRequest.respondedAt`]: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      const activityLogRef = doc(collection(db, 'tasks', taskId, 'activityLog'));
      const activityDescription = `${responderName} approved the extension request. New end date: ${formatDate(newEndDate)}.`;
      batch.set(activityLogRef, {
        timestamp: serverTimestamp(), userId: currentUser.uid, userName: responderName,
        actionType: 'extension_approved', description: activityDescription,
        changes: [{field: 'End Date', from: formatDate(task.endDate), to: formatDate(newEndDate) }]
      });

      const requesterId = task.extensionRequest.requestedBy;
      if (requesterId && requesterId !== currentUser.uid) { 
        const notificationsCol = collection(db, 'notifications');
        const message = `Your extension request for task "${task.taskName || 'Untitled Task'}" was approved by ${responderName}. The new end date is ${formatDate(newEndDate)}.`;
        const notificationRef = doc(notificationsCol);
        batch.set(notificationRef, { userId: requesterId, taskId: taskId, taskName: task.taskName || 'Untitled Task', message: message, timestamp: serverTimestamp(), isRead: false, createdBy: currentUser.uid, createdByName: responderName, type: "task_extension_approved" });
      }
      await batch.commit();
      if (isMounted.current) {
        setError("Extension request approved and task end date updated.");
        if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = setTimeout(() => { if (isMounted.current) setError(''); }, 3000);
      }
    } catch (err) {
      console.error("Error approving extension:", err);
      if(isMounted.current) setError("Failed to approve extension request.");
    } finally {
      if (isMounted.current) setIsRespondingToExtension(false);
    }
  };

  // Customer rejection
  const submitCustomerRejection = async (reason) => {
    if (!reason || !task || task.status !== 'Customer Approval' || isProcessingAction || !currentUser) {
      if(isMounted.current) setShowCustomerRejectionModal(false);
      return;
    }
    if(isMounted.current) {
      setIsCustomerRejecting(true);
      setCustomerApprovalError('');
      setCustomerApprovalSuccess('');
      setError('');
    }

    const batch = writeBatch(db);
    const taskRef = doc(db, 'tasks', taskId);
    const rejectorName = currentUser.displayName || currentUser.email || 'Admin';
    const commentText = `Reason for Customer Rejection: ${reason}`;

    try {
      const commentRef = doc(collection(db, 'tasks', taskId, 'comments'));
      batch.set(commentRef, {
        text: commentText,
        timestamp: serverTimestamp(),
        userId: currentUser.uid,
        userName: rejectorName,
        userPhotoURL: currentUser.photoURL || null,
        type: 'customer_rejection'
      });

      batch.update(taskRef, {
        status: 'In Progress',
        updatedAt: serverTimestamp(),
        customerRejectedAt: serverTimestamp(),
        customerRejectedBy: currentUser.uid,
        customerRejectionReason: reason.trim(),
        customerApprovedAt: deleteField(),
        customerApprovedBy: deleteField(),
        customerApprovalComment: deleteField(),
        companyApprovedAt: deleteField(),
        companyApprovedBy: deleteField()
      });

      const activityLogRef = doc(collection(db, 'tasks', taskId, 'activityLog'));
      const activityDescription = `${rejectorName} recorded customer rejection. Task returned to In Progress. Reason: ${reason.trim()}`;
      batch.set(activityLogRef, {
        timestamp: serverTimestamp(),
        userId: currentUser.uid,
        userName: rejectorName,
        actionType: 'task_customer_rejected',
        description: activityDescription,
        changes: [{ field: 'Status', from: 'Customer Approval', to: 'In Progress' }],
        commentId: commentRef.id
      });

      const userIdsToNotify = new Set();
      const notifiedUserIds = new Set();
      if (task.createdBy && task.createdBy !== currentUser.uid) userIdsToNotify.add(task.createdBy);
      (task.assignedTo || []).forEach(uid => userIdsToNotify.add(uid));
      if (task.companyApprovedBy && task.companyApprovedBy !== currentUser.uid && !userIdsToNotify.has(task.companyApprovedBy)) userIdsToNotify.add(task.companyApprovedBy);

      if (userIdsToNotify.size > 0) {
        const notificationsCol = collection(db, 'notifications');
        const message = `Task "${task.taskName || 'Untitled Task'}" was rejected by the customer (recorded by ${rejectorName}) and requires rework. Reason: ${reason.trim()}`;
        userIdsToNotify.forEach(notifyUserId => {
          if (!notifiedUserIds.has(notifyUserId)) {
            const notificationRef = doc(notificationsCol);
            batch.set(notificationRef, {
              userId: notifyUserId, taskId: taskId, taskName: task.taskName || 'Untitled Task',
              message: message, timestamp: serverTimestamp(), isRead: false,
              createdBy: currentUser.uid, createdByName: rejectorName, type: "task_customer_rejected"
            });
            notifiedUserIds.add(notifyUserId);
          }
        });
      }

      await batch.commit();

      if (isMounted.current) {
        setCustomerApprovalSuccess("Customer rejection recorded. Task returned to 'In Progress' for rework.");
        setShowCustomerRejectionModal(false);
        if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
        successTimeoutRef.current = setTimeout(() => { if (isMounted.current) setCustomerApprovalSuccess(''); }, 4000);
      }
    } catch (err) {
      console.error("Error processing customer rejection:", err);
      if (isMounted.current) setCustomerApprovalError("Failed to record customer rejection.");
    } finally {
      if (isMounted.current) setIsCustomerRejecting(false);
    }
  };

  // Comment add/delete
  const handleAddComment = async (e) => {
    e.preventDefault();
    const commentText = newComment.trim();
    if (!commentText) { if(isMounted.current) setCommentError("Comment cannot be empty."); return; }
    if (!currentUser) { if(isMounted.current) setCommentError("You must be logged in to comment."); return; }
    if (!task || isProcessingAction) return;

    if(isMounted.current) setIsAddingComment(true); 
    if(isMounted.current) setCommentError(''); 
    if(isMounted.current) setError('');

    const mentionedUserIds = new Set();
    const mentionRegex = /@([\w\s.-]+)/g; 
    let match;
    
    const taskUserIdsWithAccess = new Set();
    if (task.createdBy) taskUserIdsWithAccess.add(task.createdBy);
    if (task.assignedTo) (task.assignedTo || []).forEach(uid => taskUserIdsWithAccess.add(uid));
    allUsers.forEach(u => { if (u.role && managerRolesForView.includes(u.role)) { taskUserIdsWithAccess.add(u.id); } });

    while ((match = mentionRegex.exec(commentText)) !== null) {
      const mentionedName = match[1].trim().toLowerCase();
      if (!mentionedName) continue;
      const foundUser = allUsers.find(user =>
        taskUserIdsWithAccess.has(user.id) && 
        (user.name?.trim().toLowerCase() === mentionedName || user.email?.trim().toLowerCase() === mentionedName) &&
        user.id !== currentUser.uid 
      );
      if (foundUser) { mentionedUserIds.add(foundUser.id); }
    }

    const commentsRef = collection(db, 'tasks', taskId, 'comments');
    const commenterName = currentUser.displayName || currentUser.email || 'User';
    const commentData = {
      text: commentText, timestamp: serverTimestamp(), userId: currentUser.uid,
      userName: commenterName, userPhotoURL: currentUser.photoURL || null,
      mentionedUserIds: Array.from(mentionedUserIds) 
    };

    const activityLogRef = doc(collection(db, 'tasks', taskId, 'activityLog'));
    const activityDescription = `${commenterName} added a comment: "${commentText.substring(0,50)}${commentText.length > 50 ? "..." : ""}"`;

    const batch = writeBatch(db);
    const newCommentDocRef = doc(commentsRef);
    batch.set(newCommentDocRef, commentData);
    batch.set(activityLogRef, {
      timestamp: serverTimestamp(),
      userId: currentUser.uid,
      userName: commenterName,
      actionType: 'comment_added',
      description: activityDescription,
      commentId: newCommentDocRef.id 
    });
        
    // In-app (bell) notifications for the task creator, assignees, and @mentioned users (excluding
    // the commenter). Written here from the client so they appear immediately regardless of whether
    // the Cloud Functions are deployed. Doc IDs are deterministic (`${commentId}_${uid}`) so the
    // backend onTaskCommentSendPush trigger (which also sends the device push) writes the SAME docs
    // idempotently — no duplicates. The backend only creates a doc if it doesn't already exist.
    const taskNameForNotif = task.taskName || 'Untitled Task';
    const notificationsCol = collection(db, 'notifications');
    const mentionRecipients = new Set(
      Array.from(mentionedUserIds).filter(uid => uid && uid !== currentUser.uid)
    );
    const commentRecipients = new Set(
      [task.createdBy, ...(task.assignedTo || [])]
        .filter(uid => uid && uid !== currentUser.uid && !mentionRecipients.has(uid))
    );
    const addCommentNotif = (notifyUserId, message, type) => {
      const notificationRef = doc(notificationsCol, `${newCommentDocRef.id}_${notifyUserId}`);
      batch.set(notificationRef, {
        userId: notifyUserId, taskId, taskName: taskNameForNotif,
        message, link: `/task/${taskId}`, type,
        timestamp: serverTimestamp(), isRead: false,
        createdBy: currentUser.uid, createdByName: commenterName,
      });
    };
    mentionRecipients.forEach(uid =>
      addCommentNotif(uid, `${commenterName} mentioned you on task: "${taskNameForNotif}"`, 'task_mention'));
    commentRecipients.forEach(uid =>
      addCommentNotif(uid, `${commenterName} commented on task: "${taskNameForNotif}"`, 'task_comment'));

    try {
      await batch.commit();
      // Stamp the parent task doc so the (already-deployed, proven-working) onDocumentUpdated
      // trigger on tasks/{taskId} fires a DEVICE PUSH to the creator + assignees. This is done as a
      // separate best-effort write so a failure here never blocks the comment itself. Device-push
      // for comments is delivered this way (via the parent-task trigger) rather than via a
      // subcollection trigger, which was not firing reliably.
      try {
        await updateDoc(doc(db, 'tasks', taskId), {
          lastCommentId: newCommentDocRef.id,
          lastCommentBy: currentUser.uid,
          lastCommentByName: commenterName,
          lastCommentText: commentText.substring(0, 200),
          lastCommentAt: serverTimestamp(),
        });
      } catch (stampErr) {
        console.warn('Could not stamp lastComment on task (push may not fire):', stampErr?.message || stampErr);
      }
      if (isMounted.current) {
        setNewComment('');
        setShowMentionSuggestions(false);
        setMentionQuery('');
      }
    } catch (err) {
      console.error("Error adding comment or creating notifications:", err);
      if (isMounted.current) setCommentError("Failed to add comment. Please try again.");
    } finally {
      if (isMounted.current) setIsAddingComment(false);
    }
  };

  const handleDeleteComment = async (commentId, commentUserId) => { 
    if (!currentUser || isProcessingAction) { if(isMounted.current) setCommentError("Cannot delete comment now."); return; }
    const isOwner = currentUser.uid === commentUserId;
    const canManagerDelete = deleteTaskRoles.includes(userRole); 
    if (!isOwner && !canManagerDelete) { if(isMounted.current) setCommentError("You don't have permission to delete this comment."); setTimeout(() => { if(isMounted.current) setCommentError(''); }, 3000); return; }

    if (window.confirm("Are you sure you want to delete this comment? This action cannot be undone.")) {
      if(isMounted.current) {setCommentError(''); setError(''); setDeletingCommentId(commentId);}
      
      const batch = writeBatch(db);
      const commentRefToDelete = doc(db, 'tasks', taskId, 'comments', commentId);
      const activityLogRef = doc(collection(db, 'tasks', taskId, 'activityLog'));
      const commenterName = comments.find(c => c.id === commentId)?.userName || 'User'; 

      try {
        batch.delete(commentRefToDelete);
        batch.set(activityLogRef, {
          timestamp: serverTimestamp(),
          userId: currentUser.uid,
          userName: currentUser.displayName || currentUser.email,
          actionType: 'comment_deleted',
          description: `${currentUser.displayName || currentUser.email} deleted a comment by ${commenterName}.`
        });
        await batch.commit();
      } catch (err) {
        console.error("Error deleting comment or logging activity:", err);
        if (isMounted.current) {
          if (err.code === 'permission-denied') { setCommentError("Permission to delete was denied by the server."); }
          else if (err.code === 'not-found') { setCommentError("Comment not found (already deleted?)."); }
          else { setCommentError("Failed to delete comment. Please try again."); }
        }
      } finally {
        if (isMounted.current) { setDeletingCommentId(null); }
      }
    }
  };
  
  // Mention typing helpers
  const handleNewCommentChange = (e) => {
    const value = e.target.value;
    setNewComment(value); 

    const cursorPos = e.target.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPos);
    const atMatch = textBeforeCursor.match(/@([\w\s.-]*)$/); 

    if (atMatch && task && Array.isArray(allUsers) && allUsers.length > 0) { 
      const query = atMatch[1].toLowerCase(); 
      setMentionQuery(query);
      setMentionCursorPosition(cursorPos);
      
      const taskUserIdsWithAccess = new Set();
      if (task.createdBy) taskUserIdsWithAccess.add(task.createdBy);
      if (task.assignedTo) (task.assignedTo || []).forEach(uid => taskUserIdsWithAccess.add(uid)); 
      allUsers.forEach(u => { if (u.role && managerRolesForView.includes(u.role)) { taskUserIdsWithAccess.add(u.id); } });

      const suggestions = allUsers.filter(user =>
        taskUserIdsWithAccess.has(user.id) && 
        user.id !== currentUser.uid && 
        (query === '' || 
          (user.name || '').trim().toLowerCase().includes(query) || 
          (user.email || '').trim().toLowerCase().includes(query)) 
      ).slice(0, 5); 

      setMentionSuggestions(suggestions);
      setShowMentionSuggestions(suggestions.length > 0);
    } else {
      setShowMentionSuggestions(false);
      setMentionQuery('');
    }
  };

  const handleMentionSelect = (selectedUser) => {
    const currentComment = newComment;
    const lastAtSymbolIndex = currentComment.substring(0, mentionCursorPosition).lastIndexOf('@');

    if (lastAtSymbolIndex !== -1) {
      const textBeforeMention = currentComment.substring(0, lastAtSymbolIndex);
      const textAfterOriginalQueryEnd = currentComment.substring(mentionCursorPosition);
      
      const newCommentText = `${textBeforeMention}@${selectedUser.name} ${textAfterOriginalQueryEnd}`;
      setNewComment(newCommentText);

      const newCursorPos = (textBeforeMention + `@${selectedUser.name} `).length;
      requestAnimationFrame(() => {
        if(newCommentRef.current) {
          newCommentRef.current.focus();
          newCommentRef.current.setSelectionRange(newCursorPos, newCursorPos);
        }
      });
    }
    setShowMentionSuggestions(false);
    setMentionQuery('');
    setMentionSuggestions([]);
  };

  // --- NEW: Goal update prompt state + helpers/effects ---
  const [goalPromptOpen, setGoalPromptOpen] = useState(false);
  const [goalPromptLoading, setGoalPromptLoading] = useState(false);
  const [goalPromptError, setGoalPromptError] = useState('');
  const [goalClients, setGoalClients] = useState([]); // [{id, name}]
  const [clientGoalsMap, setClientGoalsMap] = useState(new Map()); // clientId -> [{id,...}]
  const [selectedGoalClientId, setSelectedGoalClientId] = useState('');
  const [selectedGoalId, setSelectedGoalId] = useState('');
  const [goalUpdateMode, setGoalUpdateMode] = useState('progress'); // 'progress' | 'current'
  const [goalProgressValue, setGoalProgressValue] = useState(100);
  const [goalCurrentDelta, setGoalCurrentDelta] = useState(1);
  const openGoalPromptOnceRef = useRef(false);

  const resolveClientIdsFromTask = useCallback(async () => {
    const ids = new Set();
    if (Array.isArray(task?.clientIds)) task.clientIds.forEach((id) => id && ids.add(id));
    if (typeof task?.clientId === 'string' && task.clientId) ids.add(task.clientId);

    if (ids.size === 0) {
      const names = Array.isArray(task?.clientNames) && task.clientNames.length > 0
        ? task.clientNames
        : (task?.clientName ? [task.clientName] : []);
      if (names.length > 0) {
        const chunks = [];
        for (let i = 0; i < names.length; i += 10) chunks.push(names.slice(i, i + 10));
        for (const chunk of chunks) {
          const qClients = query(collection(db, 'clients'), where('name', 'in', chunk));
          const snap = await getDocs(qClients);
          snap.docs.forEach((d) => ids.add(d.id));
        }
      }
    }
    return Array.from(ids);
  }, [task]);

  const loadActiveGoalsForClients = useCallback(async (clientIds) => {
    const map = new Map();
    const clientsMeta = [];
    for (const cid of clientIds) {
      try {
        const goalsCol = collection(db, 'clients', cid, 'goals');
        const q1 = query(goalsCol, orderBy('targetDate', 'asc'));
        const snap = await getDocs(q1);
        const goals = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const active = goals.filter((g) => {
          const status = (g.status || 'Planned');
          const pct = Number.isFinite(+g.progress) ? +g.progress : 0;
          return status !== 'Done' || pct < 100;
        });
        if (active.length > 0) {
          map.set(cid, active);
          clientsMeta.push({ id: cid, name: task?.clientNames?.[0] || task?.clientName || cid });
        }
      } catch (e) {
        console.warn('Failed to load goals for client', cid, e);
      }
    }
    return { map, clientsMeta };
  }, [task]);

  const maybeOpenGoalPrompt = useCallback(async () => {
    if (!task) return;
    if (openGoalPromptOnceRef.current) return;
    if (task.status !== 'Completed') return;

    try {
      setGoalPromptLoading(true);
      setGoalPromptError('');
      const clientIds = await resolveClientIdsFromTask();
      if (clientIds.length === 0) { setGoalPromptLoading(false); return; }
      const { map, clientsMeta } = await loadActiveGoalsForClients(clientIds);
      if (map.size === 0) { setGoalPromptLoading(false); return; }

      const firstClientId = clientsMeta[0]?.id || Array.from(map.keys())[0];
      const firstGoalId = map.get(firstClientId)?.[0]?.id || '';
      setClientGoalsMap(map);
      setGoalClients(clientsMeta);
      setSelectedGoalClientId(firstClientId);
      setSelectedGoalId(firstGoalId);
      setGoalProgressValue(100);
      setGoalCurrentDelta(1);
      openGoalPromptOnceRef.current = true;
      setGoalPromptOpen(true);
    } catch (e) {
      console.error('maybeOpenGoalPrompt error:', e);
      setGoalPromptError('Failed to load client goals.');
    } finally {
      setGoalPromptLoading(false);
    }
  }, [task, resolveClientIdsFromTask, loadActiveGoalsForClients]);

  useEffect(() => {
    if (!task) return;
    if (task.status === 'Completed') {
      const id = setTimeout(() => { maybeOpenGoalPrompt(); }, 150);
      return () => clearTimeout(id);
    }
  }, [task, maybeOpenGoalPrompt]);

  const applyGoalUpdate = async () => {
    if (!selectedGoalClientId || !selectedGoalId) return;
    setGoalPromptError('');

    try {
      const goals = clientGoalsMap.get(selectedGoalClientId) || [];
      const goal = goals.find((g) => g.id === selectedGoalId);
      if (!goal) { setGoalPromptError('Selected goal not found.'); return; }

      const goalRef = doc(db, 'clients', selectedGoalClientId, 'goals', selectedGoalId);
      const updates = { updatedAt: serverTimestamp() };

      if (goalUpdateMode === 'progress') {
        const clamped = Math.max(0, Math.min(100, Number(goalProgressValue || 0)));
        updates.progress = clamped;
        if (clamped >= 100 && (goal.status || 'Planned') !== 'Done') updates.status = 'Done';
      } else {
        const delta = Number(goalCurrentDelta || 0);
        const current = Number(goal.currentCount || 0);
        const target = Number(goal.targetCount || 0) || null;
        const newCurrent = Math.max(0, current + delta);
        updates.currentCount = newCurrent;
        if (target) {
          const autoPct = Math.max(0, Math.min(100, Math.round((newCurrent / target) * 100)));
          updates.progress = autoPct;
          if (autoPct >= 100 && (goal.status || 'Planned') !== 'Done') updates.status = 'Done';
        }
      }

      const activityLogRef = doc(collection(db, 'tasks', taskId, 'activityLog'));
      const updaterName = currentUser?.displayName || currentUser?.email || 'User';
      const desc =
        goalUpdateMode === 'progress'
          ? `${updaterName} updated client goal "${goal.title || selectedGoalId}" progress to ${updates.progress ?? '—'}%.`
          : `${updaterName} updated client goal "${goal.title || selectedGoalId}" current count by ${goalCurrentDelta > 0 ? '+' : ''}${goalCurrentDelta}.`;

      const batch = writeBatch(db);
      batch.update(goalRef, updates);
      batch.set(activityLogRef, {
        timestamp: serverTimestamp(),
        userId: currentUser?.uid || 'system',
        userName: updaterName,
        actionType: 'client_goal_updated',
        description: desc,
      });

      await batch.commit();

      const refreshed = new Map(clientGoalsMap);
      const arr = [...(refreshed.get(selectedGoalClientId) || [])];
      const idx = arr.findIndex((g) => g.id === selectedGoalId);
      if (idx >= 0) {
        arr[idx] = { ...arr[idx], ...updates };
        refreshed.set(selectedGoalClientId, arr);
      }
      setClientGoalsMap(refreshed);
      setGoalPromptOpen(false);
    } catch (e) {
      console.error('applyGoalUpdate failed:', e);
      setGoalPromptError('Failed to update goal.');
    }
  };

  const openPromptAfterCustomerApprove = async () => {
    try { await maybeOpenGoalPrompt(); } catch {}
  };

  // --- Derived State & Variables ---
  const canEditCoreDetails = ['Manager', 'Accounts Manager'].includes(userRole);

  if (loading) return <div className={styles.loadingIndicator}>Loading task details...</div>;
  if (error && !task && (error.includes('load') || error.includes('Auth') || error.includes('found') || error.includes('Denied') || error.includes('log in') || error.includes('missing'))) {
    return <div className={`${styles.pageContainer} ${styles.errorMessage}`}>{error}</div>;
  }
  if (!task) return <div className={`${styles.pageContainer} ${styles.errorMessage}`}>Task data unavailable or access denied. Please try again or check permissions.</div>;

  const statusSuffix = getStatusClassSuffix(task.status);
  const statusClassName = styles[`status${statusSuffix}`] || styles.statusDefault;
  const prioritySuffix = getPriorityClassSuffix(task.priority);
  const priorityClassName = styles[`priority${prioritySuffix}`] || styles.priorityDefault;
  const isSuccessFeedback = error.includes('updated') || error.includes('acknowledged') || error.includes('Opening email') || error.includes('Extension request') || error.includes('approved') || error.includes('rejected') || error.includes('submitted');
  
  const isCurrentUserCreator = task?.createdBy === currentUser?.uid;
  const isCurrentUserAssigned = (task?.assignedTo || []).includes(currentUser?.uid);

  const hasCurrentUserAcknowledged = !!task.acknowledgedBy?.[currentUser?.uid];
  const pendingExtensionRequest = task.extensionRequest?.status === 'pending';
  const canRequestExtension = isCurrentUserAssigned && !['Completed', 'Cancelled'].includes(task.status) && (!task.extensionRequest || task.extensionRequest.status === 'rejected');

  const textInputActive = submissionText.trim().length > 0;
  const linkInputActive = linkInputs.some(link => link.trim().length > 0);
  const fileInputActive = submissionFiles.length > 0;

  const canEditThisTask = canEditCoreDetails || isCurrentUserCreator;

  return (
    <div className={styles.pageContainer}>
      {/* Feedback Messages */}
      {error && !isProcessingAction && !submitSuccess && !approvalSuccess && !customerApprovalSuccess && (
        <div className={`${styles.feedbackMessage} ${isSuccessFeedback ? styles.successMessage : styles.errorMessage}`}>
          {error}
        </div>
      )}
      {submitSuccess && <div className={`${styles.feedbackMessage} ${styles.successMessage}`}>{submitSuccess}</div>}
      {approvalSuccess && <div className={`${styles.feedbackMessage} ${styles.successMessage}`}>{approvalSuccess}</div>}
      {customerApprovalSuccess && <div className={`${styles.feedbackMessage} ${styles.successMessage}`}>{customerApprovalSuccess}</div>}

      <div className={styles.detailBox}>
        <div className={styles.header}>
          <h2 className={styles.title}>{task.taskName || 'Untitled Task'}</h2>
          <div className={styles.actionButtonsContainer}>
            {assignees.length > 0 && (
              <button
                onClick={handleSendEmailNotification}
                className={`${styles.actionButton} ${styles.emailButton}`}
                disabled={isProcessingAction || isPreparingEmail}
                title="Notify Assignees"
              >
                {isPreparingEmail ? <span className={styles.loadingSpinnerSmall}></span> : 'Notify Assignees'}
              </button>
            )}

            {canEditThisTask && (
              <RouterLink
                to={`/task/${taskId}/edit`}
                className={`${styles.actionButton} ${styles.editButton} ${isProcessingAction ? styles.disabledLink : ''}`}
                onClick={(e) => { if (isProcessingAction) e.preventDefault(); }}
                aria-disabled={isProcessingAction}
                state={{ editMode: isCurrentUserCreator && !canEditCoreDetails ? 'creator' : 'manager' }}
              >
                Edit
              </RouterLink>
            )}

            {deleteTaskRoles.includes(userRole) && (
              <button
                onClick={handleDeleteTask}
                className={`${styles.actionButton} ${styles.deleteButton}`}
                disabled={isProcessingAction || isDeleting}
                title="Delete Task"
              >
                {isDeleting ? <span className={styles.loadingSpinnerSmall}></span> : 'Delete'}
              </button>
            )}
          </div>
        </div>

        {isCurrentUserAssigned && !hasCurrentUserAcknowledged && !['Completed', 'Cancelled'].includes(task.status) && (
          <div className={styles.acknowledgeSection}>
            <p>Please acknowledge this task assignment.</p>
            <button
              onClick={handleAcknowledgeTask}
              className={styles.acknowledgeButton}
              disabled={isProcessingAction || isAcknowledging}
            >
              {isAcknowledging ? <span className={styles.loadingSpinnerSmall}></span> : "Acknowledge Task"}
            </button>
          </div>
        )}

        {task.extensionRequest && (
          <div className={`${styles.extensionRequestInfo} ${styles[task.extensionRequest.status]}`}>
            <h4>Extension Request</h4>
            <p>
              Status: <strong className={styles.statusText}>{task.extensionRequest.status}</strong><br />
              Requested By: {task.extensionRequest.requestedByName || 'Assignee'}<br />
              Requested At: {formatRelativeTime(task.extensionRequest.requestedAt)}<br/>
              Requested Date: {formatDate(task.extensionRequest.requestedDate)}<br />
              {task.extensionRequest.originalEndDate && <>Original End Date: {formatDate(task.extensionRequest.originalEndDate)}<br /></>}
              Reason: {task.extensionRequest.reason || 'N/A'}<br />
              {task.extensionRequest.status !== 'pending' && task.extensionRequest.respondedAt && (
                <>Responded On: {formatRelativeTime(task.extensionRequest.respondedAt)} {task.extensionRequest.respondedBy && ` by ${findUserNameById(task.extensionRequest.respondedBy) || 'Manager'}`}<br/></>
              )}
              {task.extensionRequest.status === 'rejected' && task.extensionRequest.managerComment && <>Manager Comment: {task.extensionRequest.managerComment}<br /></>}
              {task.extensionRequest.status === 'rejected' && task.endDate && task.extensionRequest.originalEndDate &&
                task.endDate.getTime() !== new Date(task.extensionRequest.originalEndDate).getTime() && (
                  <>New End Date Set: {formatDate(task.endDate)}<br /></>
              )}
            </p>
            {pendingExtensionRequest && extensionResponseRoles.includes(userRole) && (
              <div className={styles.extensionResponseButtons}>
                <button
                  onClick={handleRejectExtension}
                  className={`${styles.actionButton} ${styles.rejectButtonExt}`}
                  disabled={isRespondingToExtension || isProcessingAction}
                >
                  Reject
                </button>
                <button
                  onClick={handleApproveExtension}
                  className={`${styles.actionButton} ${styles.approveButtonExt}`}
                  disabled={isRespondingToExtension || isProcessingAction}
                >
                  {isRespondingToExtension ? <span className={styles.loadingSpinnerSmall}></span> : 'Approve'}
                </button>
              </div>
            )}
          </div>
        )}

        {canRequestExtension && !showExtensionRequestForm && (
          <div className={styles.requestExtensionContainer}>
            <button
              onClick={() => setShowExtensionRequestForm(true)}
              className={`${styles.actionButton} ${styles.requestExtensionButton}`}
              disabled={isProcessingAction}
            >
              Request Extension
            </button>
          </div>
        )}

        {showExtensionRequestForm && (
          <form onSubmit={handleRequestExtension} className={styles.extensionRequestForm}>
            <h4>Request New End Date</h4>
            {extensionRequestError && <p className={styles.errorMessage}>{extensionRequestError}</p>}
            <div className={styles.inputGroup}>
              <label htmlFor="extensionReason">Reason (Required):</label>
              <textarea
                id="extensionReason"
                value={extensionRequestReason}
                onChange={(e) => setExtensionRequestReason(e.target.value)}
                rows={3}
                required
                disabled={isSubmittingExtensionRequest || isProcessingAction}
                placeholder="Explain why..."
                className={styles.extensionReasonInput}
              />
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="requestedEndDate">New End Date (Required):</label>
              <input
                type="date"
                id="requestedEndDate"
                value={requestedEndDate}
                onChange={(e) => setRequestedEndDate(e.target.value)}
                required
                min={formatDate(new Date((task.endDate || Date.now()) + 24*60*60*1000), 'yyyy-MM-dd')}
                disabled={isSubmittingExtensionRequest || isProcessingAction}
                className={styles.dateInput}
              />
            </div>
            <div className={styles.extensionFormActions}>
              <button
                type="button"
                onClick={() => { setShowExtensionRequestForm(false); setRequestedEndDate(''); setExtensionRequestReason(''); setExtensionRequestError(''); }}
                className={`${styles.actionButton} ${styles.cancelButtonExt}`}
                disabled={isSubmittingExtensionRequest || isProcessingAction}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`${styles.actionButton} ${styles.submitButtonExt}`}
                disabled={isSubmittingExtensionRequest || isProcessingAction || !requestedEndDate || !extensionRequestReason.trim()}
              >
                {isSubmittingExtensionRequest ? <span className={styles.loadingSpinnerSmall}></span> : 'Submit Request'}
              </button>
            </div>
          </form>
        )}

        <div className={styles.detailsGrid}>
          <div className={styles.detailItemFull}>
            <strong className={styles.detailLabel}>Description:</strong>
            <p className={styles.detailValueDescription} style={{ whiteSpace: 'pre-wrap' }}>
              {task.description || 'N/A'}
            </p>
          </div>

          <div className={`${styles.detailItem} ${styles.statusSection}`}>
            <strong className={styles.detailLabel}>Status:</strong>
            <div className={styles.statusDisplay}>
              <span className={`${styles.statusBadge} ${statusClassName}`}>{task.status}</span>
            </div>

            {statusUpdateRoles.includes(userRole) &&
              !(task.status === 'Cancelled' && !['Manager', 'Accounts Manager'].includes(userRole)) && (
              <div className={styles.statusChangeSection}>
                <label htmlFor="status-select" className={styles.srOnly}>Change Status:</label>
                <select
                  id="status-select"
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className={styles.statusSelect}
                  disabled={isProcessingAction || updatingStatus}
                >
                  {statusOptions.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
                <button
                  onClick={handleStatusChange}
                  disabled={isProcessingAction || updatingStatus || newStatus === task.status}
                  className={styles.updateButton}
                >
                  {updatingStatus ? <span className={styles.loadingSpinnerSmall}></span> : 'Update'}
                </button>
              </div>
            )}

            {task.status === 'Cancelled' && !statusUpdateRoles.includes(userRole) && (
              <p className={styles.statusLockedMsg}>Task Cancelled.</p>
            )}
          </div>

          <div className={styles.detailItem}>
            <strong className={styles.detailLabel}>Client(s):</strong>
            <span className={styles.detailValue}>
              {task.clientNames && task.clientNames.length > 0
                ? task.clientNames.join(', ')
                : (task.clientName || 'N/A')}
            </span>
          </div>

          <div className={styles.detailItem}>
            <strong className={styles.detailLabel}>Priority:</strong>
            <span className={`${styles.priorityBadge} ${priorityClassName}`}>
              {task.priority || 'Medium'}
            </span>
          </div>

          <div className={styles.detailItem}>
            <strong className={styles.detailLabel}>Start Date:</strong>
            <span className={styles.detailValue}>{formatDate(task.startDate)}</span>
          </div>

          <div className={styles.detailItem}>
            <strong className={styles.detailLabel}>End Date:</strong>
            <span className={styles.detailValue}>{formatDate(task.endDate)}</span>
          </div>

          {/* Assignees with click-to-profile modal */}
          <div className={`${styles.detailItem} ${styles.assigneesItem}`}>
            <strong className={styles.detailLabel}>Assigned To:</strong>
            {assignees.length > 0 ? (
              <div className={styles.assigneeList}>
                {assignees.map(assignee => {
                  const firstViewedTimestamp = task.viewedBy?.[assignee.id]?.toDate?.();
                  const acknowledgedTimestamp = task.acknowledgedBy?.[assignee.id]?.toDate?.();
                  const hasViewed = !!firstViewedTimestamp;
                  const hasAcknowledged = !!acknowledgedTimestamp;
                  const viewStatusText = hasViewed ? `Viewed ${formatRelativeTime(firstViewedTimestamp)}` : 'Not viewed';
                  const ackStatusText = hasAcknowledged ? `Acknowledged` : '(Pending Ack)';
                  const responseTime = (task.createdAt && hasAcknowledged)
                    ? formatDurationBetweenDates(task.createdAt, acknowledgedTimestamp)
                    : '';

                  const AssigneeContent = () => (
                    <div
                      className={styles.assigneeChip}
                      title={assignee.email || assignee.id}
                      role="button"
                      tabIndex={0}
                      onClick={() => openStaffModal(assignee.id)}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openStaffModal(assignee.id); }}
                    >
                      <img
                        src={assignee.photoURL || DEFAULT_AVATAR}
                        alt={assignee.name || 'Assignee'}
                        className={styles.assigneeAvatar}
                        onError={(e) => {
                          e.currentTarget.onerror = null;
                          e.currentTarget.src = DEFAULT_AVATAR;
                        }}
                      />
                      <span className={styles.assigneeName}>{assignee.name}</span>
                    </div>
                  );

                  return (
                    <div key={assignee.id} className={styles.assigneeChipContainer}>
                      <AssigneeContent />
                      {managerRolesForView.includes(userRole) && (
                        <div className={styles.managerStatusInfo}>
                          <span
                            className={`${styles.viewStatus} ${hasViewed ? styles.viewed : styles.notViewed}`}
                            title={hasViewed ? `First viewed: ${firstViewedTimestamp?.toLocaleString()}` : 'Not viewed'}
                          >
                            {viewStatusText}
                          </span>
                          <span
                            className={`${styles.ackStatus} ${hasAcknowledged ? styles.acknowledged : styles.notAcknowledged}`}
                            title={hasAcknowledged ? `Acknowledged at ${acknowledgedTimestamp?.toLocaleString()}` : 'Not acknowledged'}
                          >
                            {ackStatusText}
                            {responseTime && <span className={styles.responseTime}> ({responseTime})</span>}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <span className={styles.detailValue}>None</span>
            )}
          </div>
        </div>

        {/* Creator – click to open profile modal */}
        <div className={styles.metaInfo}>
          <span className={styles.creatorInfo}>
            Created By:{' '}
            <span
              className={styles.creatorClickable}
              role="button"
              tabIndex={0}
              onClick={() => creatorInfo?.id && openStaffModal(creatorInfo.id)}
              onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && creatorInfo?.id) openStaffModal(creatorInfo.id); }}
              title="View profile"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: creatorInfo?.id ? 'pointer' : 'default' }}
            >
              <img
                src={creatorInfo.photoURL || DEFAULT_AVATAR}
                alt={creatorInfo.name || ''}
                className={styles.creatorAvatar}
                onError={(e)=>{e.currentTarget.onerror=null; e.currentTarget.src=DEFAULT_AVATAR}}
              />
              <span>{creatorInfo.name || (task.createdBy ? '...' : 'N/A')}</span>
            </span>
          </span>
          <span title={task.createdAt?.toLocaleString()}>Created: {formatRelativeTime(task.createdAt)}</span>
          <span title={task.updatedAt?.toLocaleString()}>Last Updated: {formatRelativeTime(task.updatedAt)}</span>
        </div>

        {(isCurrentUserAssigned || isCurrentUserCreator) &&
          ['Not Started', 'In Progress'].includes(task.status) && (
          <div className={styles.submissionSection}>
            <h3 className={styles.subTitle}>Submit Your Work</h3>
            {submitError && <p className={`${styles.errorMessage} ${styles.submitError}`}>{submitError}</p>}
            {submitSuccess && <p className={`${styles.successMessage} ${styles.submitSuccess}`}>{submitSuccess}</p>}

            <form onSubmit={handleSubmitWork} className={styles.submissionForm}>
              <div className={styles.inputGroup}>
                <label htmlFor="submissionText">Notes/Text Description:</label>
                <textarea
                  id="submissionText"
                  value={submissionText}
                  onChange={handleSubmissionTextChange}
                  placeholder="Enter your text submission here..."
                  rows="4"
                  className={styles.submissionTextarea}
                  disabled={isSubmittingWork || isProcessingAction || linkInputActive || fileInputActive}
                  aria-label="Submission text notes"
                />
              </div>

              <p className={styles.orSeparator}>OR</p>

              <div className={styles.linkInputArea}>
                <label>Link(s) (Max 10):</label>
                {linkInputs.map((link, index) => (
                  <div key={index} className={styles.linkInputGroup}>
                    <input
                      type="url"
                      value={link}
                      onChange={(e) => handleLinkInputChange(index, e.target.value)}
                      placeholder={`https://... (Link ${index + 1})`}
                      disabled={isSubmittingWork || isProcessingAction || textInputActive || fileInputActive}
                      className={styles.submissionInput}
                      aria-label={`Link ${index + 1}`}
                    />
                    {linkInputs.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeLinkInput(index)}
                        className={styles.removeLinkButton}
                        disabled={isSubmittingWork || isProcessingAction || textInputActive || fileInputActive}
                        aria-label={`Remove Link ${index + 1}`}
                      >
                        &times;
                      </button>
                    )}
                  </div>
                ))}
                {linkInputs.length < 10 && (
                  <button
                    type="button"
                    onClick={addLinkInput}
                    className={styles.addLinkButton}
                    disabled={isSubmittingWork || isProcessingAction || textInputActive || fileInputActive}
                  >
                    + Add Link
                  </button>
                )}
              </div>

              <p className={styles.orSeparator}>OR</p>

              <div className={styles.inputGroup}>
                <label htmlFor="submissionFile">
                  Upload File(s) (Max {MAX_UPLOAD_COUNT}, {MAX_UPLOAD_SIZE_MB}MB/file)
                  <br />
                  <small>(Allowed: jpg, png, gif, webp, pdf, zip, rar, 7z)</small>
                </label>
                <input
                  type="file"
                  id="submissionFile"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  accept="image/jpeg,image/png,image/gif,image/webp,application/pdf,application/zip,application/x-rar-compressed,application/vnd.rar,application/x-7z-compressed"
                  disabled={isSubmittingWork || isProcessingAction || textInputActive || linkInputActive}
                  className={styles.fileInput}
                  multiple
                  aria-label={`Upload files, max ${MAX_UPLOAD_COUNT}`}
                />
              </div>

              {filePreviews.length > 0 && (
                <div className={styles.filePreviewContainer}>
                  <p>Selected ({filePreviews.length}/{MAX_UPLOAD_COUNT}):</p>
                  <div className={styles.filePreviewList}>
                    {filePreviews.map((preview, index) => (
                      <div key={index} className={styles.filePreviewItem}>
                        {preview.url ? (
                          <img src={preview.url} alt={`Preview ${preview.name}`} className={styles.imagePreview} />
                        ) : (
                          <span className={styles.fileNamePreview} title={preview.name}>
                            {preview.name.length > 25 ? `${preview.name.substring(0, 22)}...` : preview.name}
                          </span>
                        )}
                        <span className={styles.fileSizePreview}>
                          {`(${(preview.size / (1024 * 1024)).toFixed(2)} MB)`}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          className={styles.removeSingleFileButton}
                          title="Remove file"
                          aria-label={`Remove ${preview.name}`}
                          disabled={isSubmittingWork || isProcessingAction}
                        >
                          &times;
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <button
                type="submit"
                className={`${styles.formButton} ${styles.submitButton}`}
                disabled={isSubmittingWork || isProcessingAction || (!textInputActive && !linkInputActive && !fileInputActive)}
              >
                {isUploadingFile ? 'Uploading...' : (isSubmittingWork ? 'Submitting...' : 'Submit for Approval')}
              </button>
            </form>
          </div>
        )}

        {(task.submissionTextData || task.submissionLinks?.length > 0 || task.submissionFilesData?.length > 0) &&
          ['In Progress', 'Company Approval', 'Customer Approval', 'Completed'].includes(task.status) &&
          task.submittedAt && (
          <div className={`${styles.submissionSection} ${styles.submittedWorkInfo}`}>
            <h4 className={styles.subTitleSmall}>
              {task.status === 'In Progress' && (task.lastRejectionReason || task.customerRejectionReason)
                ? 'Previously Submitted Work (Rejected)'
                : 'Submitted Work'}
            </h4>

            {task.submissionTextData && (
              <div className={styles.submittedText}>
                <strong>Notes/Text:</strong>
                <p style={{ whiteSpace: 'pre-wrap' }}>{task.submissionTextData}</p>
              </div>
            )}

            {task.submissionLinks?.length > 0 && (
              <div className={styles.submittedLinks}>
                <strong>Links:</strong>
                <ul>
                  {task.submissionLinks.map((link, index) => (
                    <li key={index}><a href={link} target="_blank" rel="noopener noreferrer">{link}</a></li>
                  ))}
                </ul>
              </div>
            )}

            {task.submissionFilesData?.length > 0 && (
              <div className={styles.submittedFileContainer}>
                <strong>Files:</strong>
                <div className={styles.submittedFileList}>
                  {task.submissionFilesData.map((fileData, index) => (
                    <a
                      key={index}
                      href={fileData.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.submittedFileLink}
                      title={`View: ${fileData.name}`}
                    >
                      {fileData.type?.startsWith('image/') ? (
                        <img src={fileData.url} alt={`Preview ${fileData.name}`} className={styles.submittedImageThumbnail} />
                      ) : (
                        <span className={styles.submittedFileName} title={fileData.name}>
                          {fileData.name.length > 20 ? `${fileData.name.substring(0, 17)}...` : fileData.name}
                          {' '}
                          ({(fileData.size / (1024 * 1024)).toFixed(2)} MB)
                        </span>
                      )}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {task.submittedAt && (
              <p className={styles.submittedMeta}>
                <small>
                  Submitted: {formatDate(task.submittedAt, { dateStyle: 'medium', timeStyle: 'short' })}
                  {task.submittedBy && ` by ${findUserNameById(task.submittedBy) || 'Assignee'}`}
                </small>
              </p>
            )}

            {task.lastRejectionReason && task.status === 'In Progress' && (
              <div className={styles.rejectionReasonDisplay}>
                <strong>Last Company Rejection:</strong>
                <p>{task.lastRejectionReason}</p>
                <small>By: {findUserNameById(task.lastRejectedBy)} at {formatDate(task.lastRejectedAt)}</small>
              </div>
            )}

            {task.customerRejectionReason && task.status === 'In Progress' && (
              <div className={styles.rejectionReasonDisplay}>
                <strong>Last Customer Rejection:</strong>
                <p>{task.customerRejectionReason}</p>
                <small>Recorded By: {findUserNameById(task.customerRejectedBy)} at {formatDate(task.customerRejectedAt)}</small>
              </div>
            )}
          </div>
        )}

        {!(isCurrentUserAssigned || isCurrentUserCreator) && ['Not Started', 'In Progress'].includes(task.status) && (
          <div className={`${styles.submissionSection} ${styles.submissionInfo}`}>
            <p>Work submission is handled by the assignee(s) or task creator.</p>
          </div>
        )}

        {(isCurrentUserAssigned || isCurrentUserCreator) &&
          ['Completed', 'Cancelled', 'Company Approval', 'Customer Approval'].includes(task.status) && (
          <div className={`${styles.submissionSection} ${styles.submissionInfo}`}>
            <p>Cannot submit work when task is "{task.status}".</p>
          </div>
        )}

        {task.status === 'Company Approval' && (
          <div className={styles.approvalSection}>
            <h3 className={styles.subTitle}>Company Approval</h3>
            {approvalError && <p className={styles.errorMessage}>{approvalError}</p>}
            {approvalSuccess && <p className={styles.successMessage}>{approvalSuccess}</p>}

            {companyApprovalRoles.includes(userRole) ? (
              <>
                <p>Review submitted work and approve or reject.</p>
                <div className={styles.approvalButtons}>
                  <button
                    onClick={handleReject}
                    className={`${styles.formButton} ${styles.rejectButton}`}
                    disabled={isProcessingAction || isRejecting || isApproving}
                  >
                    Reject
                  </button>
                  <button
                    onClick={handleApprove}
                    className={`${styles.formButton} ${styles.approveButton}`}
                    disabled={isProcessingAction || isRejecting || isApproving}
                  >
                    {isApproving ? <span className={styles.loadingSpinnerSmall}></span> : 'Approve Internally'}
                  </button>
                </div>
              </>
            ) : (
              <p>Awaiting internal approval.</p>
            )}
          </div>
        )}

        {task.status === 'Customer Approval' && (
          <div className={`${styles.approvalSection} ${styles.customerApprovalSection}`}>
            <h3 className={styles.subTitle}>Customer Approval</h3>
            {customerApprovalError && <p className={styles.errorMessage}>{customerApprovalError}</p>}
            {customerApprovalSuccess && <p className={styles.successMessage}>{customerApprovalSuccess}</p>}

            {customerApprovalRoles.includes(userRole) ? (
              <>
                <p>Confirm customer feedback and finalize.</p>
                {task.companyApprovedAt && (
                  <p>
                    <small>
                      Company Approved: {formatDate(task.companyApprovedAt)}
                      {task.companyApprovedBy && ` by ${findUserNameById(task.companyApprovedBy) || 'Admin'}`}
                    </small>
                  </p>
                )}
                <div className={styles.approvalButtons}>
                  <button
                    onClick={handleCustomerReject}
                    className={`${styles.formButton} ${styles.rejectButton}`}
                    disabled={isProcessingAction || isCustomerRejecting || isCustomerApproving}
                  >
                    Reject (Customer)
                  </button>
                  <button
                    onClick={handleCustomerApprove}
                    className={`${styles.formButton} ${styles.approveButton}`}
                    disabled={isProcessingAction || isCustomerRejecting || isCustomerApproving}
                  >
                    {isCustomerApproving ? <span className={styles.loadingSpinnerSmall}></span> : 'Approve (Complete)'}
                  </button>
                </div>
              </>
            ) : (
              <p>Awaiting final approval based on customer feedback.</p>
            )}
          </div>
        )}

        {/* Comments */}
        <div className={styles.commentsSection}>
          <h3 className={styles.commentsTitle}>Comments ({comments.length})</h3>
          {commentError && <p className={`${styles.errorMessage} ${styles.commentError}`}>{commentError}</p>}
          {commentsLoading ? (
            <p className={styles.loadingIndicatorSmall}>Loading comments...</p>
          ) : (
            <ul className={styles.commentList}>
              {comments.length === 0 && <li className={styles.noComments}>No comments yet.</li>}
              {comments.map(comment => (
                <li
                  key={comment.id}
                  className={`${styles.commentItem}
                    ${comment.type === 'rejection' || comment.type === 'customer_rejection' ? styles.rejectionComment : ''}
                    ${comment.type === 'customer_approval' ? styles.approvalComment : ''}`}
                >
                  <img
                    src={comment.userPhotoURL || DEFAULT_AVATAR}
                    alt={comment.userName || 'User'}
                    className={styles.commentAvatar}
                    onError={(e)=>{e.currentTarget.onerror=null; e.currentTarget.src=DEFAULT_AVATAR}}
                    role="button"
                    tabIndex={0}
                    title="View profile"
                    onClick={() => comment.userId && openStaffModal(comment.userId)}
                    onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && comment.userId) openStaffModal(comment.userId); }}
                  />
                  <div className={styles.commentBody}>
                    <div className={styles.commentHeader}>
                      <strong
                        className={styles.commentAuthor}
                        role="button"
                        tabIndex={0}
                        title="View profile"
                        onClick={() => comment.userId && openStaffModal(comment.userId)}
                        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && comment.userId) openStaffModal(comment.userId); }}
                      >
                        {comment.userName || 'User'}
                      </strong>
                      <span className={styles.commentTimestamp} title={comment.timestamp?.toLocaleString()}>
                        {comment.timestamp ? formatRelativeTime(comment.timestamp) : ''}
                      </span>
                      {(currentUser?.uid === comment.userId || deleteTaskRoles.includes(userRole)) && (
                        <button
                          onClick={() => handleDeleteComment(comment.id, comment.userId)}
                          className={styles.commentDeleteButton}
                          title="Delete comment"
                          disabled={deletingCommentId === comment.id || isProcessingAction}
                        >
                          {deletingCommentId === comment.id
                            ? <span className={styles.loadingSpinnerSmall}></span>
                            : <>&times;</>}
                        </button>
                      )}
                    </div>
                    <p className={styles.commentText} style={{ whiteSpace: 'pre-wrap' }}>{comment.text}</p>
                  </div>
                </li>
              ))}
              <div ref={commentsEndRef} />
            </ul>
          )}

          <form onSubmit={handleAddComment} className={styles.addCommentForm}>
            <div className={styles.commentInputContainer}>
              {showMentionSuggestions && mentionSuggestions.length > 0 && (
                <ul className={styles.mentionSuggestionsList}>
                  {mentionSuggestions.map(user => (
                    <li
                      key={user.id}
                      onClick={() => handleMentionSelect(user)}
                      className={styles.mentionSuggestionItem}
                      role="option"
                      aria-selected="false"
                    >
                      <img
                        src={user.photoURL || DEFAULT_AVATAR}
                        alt={user.name}
                        className={styles.mentionSuggestionAvatar}
                        onError={(e)=>{e.currentTarget.onerror=null; e.currentTarget.src=DEFAULT_AVATAR}}
                      />
                      <div className={styles.mentionSuggestionInfo}>
                        <span className={styles.mentionSuggestionName}>{user.name}</span>
                        <span className={styles.mentionSuggestionEmail}>{user.email}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
              <textarea
                ref={newCommentRef}
                value={newComment}
                onChange={handleNewCommentChange}
                placeholder="Add a comment... Use @ to mention (e.g., @Jane Doe)"
                rows="3"
                className={styles.commentInput}
                disabled={isAddingComment || isProcessingAction}
                required
                aria-label="Add a new comment"
              />
            </div>
            <button
              type="submit"
              className={styles.commentSubmitButton}
              disabled={isAddingComment || isProcessingAction || !newComment.trim()}
            >
              {isAddingComment ? <span className={styles.loadingSpinnerSmall}></span> : 'Add Comment'}
            </button>
          </form>
        </div>

        {/* Activity Log */}
        {managerRolesForView.includes(userRole) && (
          <div className={styles.activityLogSection}>
            <h3 className={styles.activityLogTitle}>Task Activity Log</h3>
            {activityLoading && <div className={styles.loadingIndicatorSmall}>Loading activity...</div>}
            {activityError && <p className={styles.errorMessage}>{activityError}</p>}
            {!activityLoading && !activityError && activityLog.length === 0 && (
              <p className={styles.noActivityMessage}>No activity recorded for this task yet.</p>
            )}
            {!activityLoading && !activityError && activityLog.length > 0 && (
              <ul className={styles.activityList}>
                {activityLog.map(entry => (
                  <li key={entry.id} className={styles.activityItem}>
                    <div className={styles.activityHeader}>
                      <strong
                        className={styles.activityUser}
                        role="button"
                        tabIndex={0}
                        title="View profile"
                        onClick={() => entry.userId && openStaffModal(entry.userId)}
                        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && entry.userId) openStaffModal(entry.userId); }}
                      >
                        {entry.userName || 'System'}
                      </strong>
                      <span className={styles.activityTimestamp}>
                        {entry.timestamp ? formatRelativeTime(entry.timestamp) : 'N/A'}
                        {entry.timestamp && (
                          <span className={styles.tooltiptext}>
                            {formatDate(entry.timestamp, { dateStyle: 'full', timeStyle: 'medium' })}
                          </span>
                        )}
                      </span>
                    </div>
                    <p className={styles.activityDescription}>{entry.description}</p>
                    {entry.changes && entry.changes.length > 0 && (
                      <div className={styles.activityChanges}>
                        <strong>Details of changes:</strong>
                        <ul>
                          {entry.changes.map((change, index) => (
                            <li key={index}>
                              <strong>{change.field}:</strong>
                              {change.from !== undefined && (
                                <span className={styles.changeOld}>
                                  {' '}From "{String(change.from).substring(0, 100)}{String(change.from).length > 100 ? '...' : ''}"
                                </span>
                              )}
                              <span className={styles.changeNew}>
                                {' '}To "{String(change.to).substring(0, 100)}{String(change.to).length > 100 ? '...' : ''}"
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* ========= Staff Profile Modal ========= */}
      {staffModalOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="staff-modal-title"
          onClick={(e) => { if (e.target === e.currentTarget) closeStaffModal(); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
        >
          <div
            style={{
              background: '#fff', width: 'min(520px, 92vw)', borderRadius: 12,
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)', padding: 20, position: 'relative'
            }}
          >
            <button
              onClick={closeStaffModal}
              aria-label="Close"
              style={{
                position: 'absolute', right: 10, top: 8, border: 'none',
                background: 'transparent', fontSize: 22, cursor: 'pointer', lineHeight: 1
              }}
            >
              &times;
            </button>

            <h3 id="staff-modal-title" style={{ marginTop: 4, marginBottom: 16 }}>Staff Info</h3>

            {staffLoading ? (
              <div className={styles.loadingIndicatorSmall}>Loading…</div>
            ) : staffError ? (
              <p className={styles.errorMessage}>{staffError}</p>
            ) : staffProfile ? (
              <div style={{ display: 'grid', gridTemplateColumns: '96px 1fr', gap: 16 }}>
                <img
                  src={staffProfile.photoURL || DEFAULT_AVATAR}
                  alt={staffProfile.name || 'Staff'}
                  style={{ width: 96, height: 96, borderRadius: '50%', objectFit: 'cover', border: '1px solid #eee' }}
                  onError={(e)=>{e.currentTarget.onerror=null; e.currentTarget.src=DEFAULT_AVATAR}}
                />
                <div style={{ display: 'grid', gap: 6 }}>
                  <div><strong>Name:</strong> {staffProfile.name || 'Unknown'}</div>
                  <div><strong>Email:</strong> {staffProfile.email || 'N/A'}</div>
                  {/* <div><strong>Role:</strong> {staffProfile.role || 'N/A'}</div> */}
                  {staffProfile.phone && <div><strong>Phone:</strong> {staffProfile.phone}</div>}
                  {staffProfile.department && <div><strong>Department:</strong> {staffProfile.department}</div>}
                  {staffProfile.joinedAt && <div><strong>Joined:</strong> {formatDate(staffProfile.joinedAt)}</div>}
                </div>
              </div>
            ) : (
              <p>No profile available.</p>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18 }}>
              <button
                onClick={closeStaffModal}
                className={styles.actionButton}
                style={{ background: '#e5e7eb', color: '#111827' }}
              >
                Close
              </button>
              <button
                disabled={!staffProfile?.id}
                onClick={() => {
                  if (!staffProfile?.id) return;
                  navigate(
                    `/growup-chat?to=${encodeURIComponent(staffProfile.id)}`,
                    {
                      state: {
                        toUserId: staffProfile.id,
                        toName: staffProfile.name || '',
                        toPhotoURL: staffProfile.photoURL || ''
                      },
                      replace: false
                    }
                  );
                }}
                className={styles.actionButton}
              >
                Message in Chat
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ========= End Staff Profile Modal ========= */}

      {/* ========= NEW: Goal Update Prompt ========= */}
      {goalPromptOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="goal-prompt-title"
          onClick={(e) => { if (e.target === e.currentTarget) setGoalPromptOpen(false); }}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}
        >
          <div
            style={{
              background: '#fff', width: 'min(560px, 94vw)', borderRadius: 12,
              boxShadow: '0 10px 30px rgba(0,0,0,0.2)', padding: 20, position: 'relative'
            }}
          >
            <button
              onClick={() => setGoalPromptOpen(false)}
              aria-label="Close"
              style={{
                position: 'absolute', right: 10, top: 8, border: 'none',
                background: 'transparent', fontSize: 22, cursor: 'pointer', lineHeight: 1
              }}
            >
              &times;
            </button>

            <h3 id="goal-prompt-title" style={{ marginTop: 4, marginBottom: 16 }}>
              Update a Client Goal?
            </h3>
            <p style={{ marginTop: 0 }}>
              This task is <strong>Completed</strong>. We found active goals for this client. Would you like to update a goal's
              <em> progress percentage</em> or <em>current count</em>?
            </p>

            {goalPromptError && <p className={styles.errorMessage} style={{ marginTop: 8 }}>{goalPromptError}</p>}
            {goalPromptLoading ? (
              <div className={styles.loadingIndicatorSmall}>Loading goals…</div>
            ) : (
              <>
                <div style={{ display: 'grid', gap: 10, marginTop: 12 }}>
                  <label style={{ display: 'grid', gap: 6 }}>
                    <span><strong>Client</strong></span>
                    <select
                      value={selectedGoalClientId}
                      onChange={(e) => {
                        const newClientId = e.target.value;
                        setSelectedGoalClientId(newClientId);
                        const firstGoalForClient = (clientGoalsMap.get(newClientId) || [])[0]?.id || '';
                        setSelectedGoalId(firstGoalForClient);
                      }}
                      className={styles.statusSelect}
                    >
                      {(goalClients.length > 0 ? goalClients : [{ id: selectedGoalClientId, name: task.clientName || 'Client' }]).map(c => (
                        <option key={c.id} value={c.id}>{c.name || c.id}</option>
                      ))}
                    </select>
                  </label>

                  <label style={{ display: 'grid', gap: 6 }}>
                    <span><strong>Goal</strong></span>
                    <select
                      value={selectedGoalId}
                      onChange={(e) => setSelectedGoalId(e.target.value)}
                      className={styles.statusSelect}
                    >
                      {(clientGoalsMap.get(selectedGoalClientId) || []).map(g => (
                        <option key={g.id} value={g.id}>
                          {(g.title || g.name || g.id) + (Number.isFinite(+g.progress) ? ` (${g.progress}% )` : '')}
                        </option>
                      ))}
                    </select>
                  </label>

                  <fieldset style={{ border: '1px solid #e5e7eb', borderRadius: 8, padding: 10 }}>
                    <legend style={{ padding: '0 6px' }}>Update Mode</legend>
                    <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="radio"
                          name="goalUpdateMode"
                          value="progress"
                          checked={goalUpdateMode === 'progress'}
                          onChange={() => setGoalUpdateMode('progress')}
                        />
                        Progress (%)
                      </label>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        <input
                          type="radio"
                          name="goalUpdateMode"
                          value="current"
                          checked={goalUpdateMode === 'current'}
                          onChange={() => setGoalUpdateMode('current')}
                        />
                        Current Count (Δ)
                      </label>
                    </div>

                    {goalUpdateMode === 'progress' ? (
                      <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
                        <label>
                          <span style={{ display: 'block', marginBottom: 6 }}>Set progress percent (0–100)</span>
                          <input
                            type="number"
                            min={0}
                            max={100}
                            value={goalProgressValue}
                            onChange={(e) => setGoalProgressValue(e.target.value)}
                            className={styles.submissionInput}
                            style={{ maxWidth: 160 }}
                          />
                        </label>
                      </div>
                    ) : (
                      <div style={{ display: 'grid', gap: 6, marginTop: 10 }}>
                        <label>
                          <span style={{ display: 'block', marginBottom: 6 }}>Change current count by (e.g., 1, 5, -2)</span>
                          <input
                            type="number"
                            value={goalCurrentDelta}
                            onChange={(e) => setGoalCurrentDelta(e.target.value)}
                            className={styles.submissionInput}
                            style={{ maxWidth: 160 }}
                          />
                        </label>
                        <small style={{ color: '#6b7280' }}>
                          If the goal has a target count, progress will auto-recalculate.
                        </small>
                      </div>
                    )}
                  </fieldset>
                </div>

                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
                  <button
                    onClick={() => setGoalPromptOpen(false)}
                    className={styles.actionButton}
                    style={{ background: '#e5e7eb', color: '#111827' }}
                  >
                    Not Now
                  </button>
                  <button
                    onClick={applyGoalUpdate}
                    className={styles.actionButton}
                  >
                    Apply Update
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {/* ========= End Goal Update Prompt ========= */}

      {/* Modals */}
      <RejectionModal
        isOpen={showRejectionModal}
        onClose={() => setShowRejectionModal(false)}
        onSubmit={submitRejection}
        taskName={task?.taskName}
        isSubmitting={isRejecting}
        promptTitle="Company Rejection Reason"
        placeholderText="Provide reason for internal rejection..."
      />

      <RejectionModal
        isOpen={showCustomerRejectionModal}
        onClose={() => setShowCustomerRejectionModal(false)}
        onSubmit={submitCustomerRejection}
        taskName={task?.taskName}
        isSubmitting={isCustomerRejecting}
        promptTitle="Customer Rejection Reason"
        placeholderText="Enter reason provided by the customer..."
      />

      <ExtensionRejectionModal
        isOpen={showExtensionRejectionModal}
        onClose={() => setShowExtensionRejectionModal(false)}
        onSubmit={submitExtensionRejection}
        taskName={task?.taskName}
        currentEndDate={task?.endDate}
        requestedEndDate={task?.extensionRequest?.requestedDate}
        isSubmitting={isRespondingToExtension}
      />
    </div>
  );
};

export default TaskDetailPage;
